import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { collection, doc, getDocs, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { app, db } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getOrg } from "../utils/tenant";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from "date-fns";
import { useLocation, useNavigate } from "react-router-dom";
import { Toast } from "../components/UIComponents";
import { useBoundStoreName } from "../hooks/useBoundStore";
import { useTheme } from "../theme/ThemeProvider";

import { formatDurationMinutes } from "../utils/durationFormat";
import {
  fieldsForSubmission,
  isMorpheusField,
  morpheusResultForField,
  restoreLegacyMorpheusSubmission,
} from "../utils/morpheusSubmission";
const CLOCKIN_VIRTUAL_ID = "CLOCKIN_VIRTUAL";

function tsToDate(v) {
  return v?.toDate
    ? v.toDate()
    : v?.seconds
    ? new Date(v.seconds * 1000)
    : v
    ? new Date(v)
    : null;
}


function reportActiveRange(periodKey = "", dateFilter = "") {
  const today = new Date();
  const mondayOptions = { weekStartsOn: 1 };

  if (periodKey === "thisWeek") {
    return {
      start: startOfWeek(today, mondayOptions),
      end: endOfWeek(today, mondayOptions),
      label: "this week",
    };
  }

  if (periodKey === "lastWeek") {
    const ref = subWeeks(today, 1);
    return {
      start: startOfWeek(ref, mondayOptions),
      end: endOfWeek(ref, mondayOptions),
      label: "last week",
    };
  }

  if (periodKey === "thisMonth") {
    return {
      start: startOfMonth(today),
      end: endOfMonth(today),
      label: "this month",
    };
  }

  if (periodKey === "lastMonth") {
    const ref = subMonths(today, 1);
    return {
      start: startOfMonth(ref),
      end: endOfMonth(ref),
      label: "last month",
    };
  }

  const selected = dateFilter ? new Date(`${dateFilter}T12:00:00`) : today;
  const safe = Number.isNaN(selected.getTime()) ? today : selected;
  return {
    start: safe,
    end: safe,
    label: dateFilter || format(safe, "yyyy-MM-dd"),
  };
}

function reportDateMatchesRange(value, periodKey = "", dateFilter = "") {
  const d = value instanceof Date ? value : tsToDate(value);
  if (!d || Number.isNaN(d.getTime())) return false;

  const range = reportActiveRange(periodKey, dateFilter);
  const start = new Date(range.start);
  const end = new Date(range.end);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  return d.getTime() >= start.getTime() && d.getTime() <= end.getTime();
}

function reportRangeHeading(periodKey = "", dateFilter = "") {
  return reportActiveRange(periodKey, dateFilter).label;
}

function formatHoursMinutesOnly(totalMinutes = 0) {
  const safeMinutes = Math.max(0, Math.round(Number(totalMinutes) || 0));
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;
  if (!hours) return `${minutes}M`;
  if (!minutes) return `${hours}H`;
  return `${hours}H ${minutes}M`;
}

function subheadingSaveMeta(subheadingSaves, subheading, fallbackIdx = 0) {
  if (!subheadingSaves || typeof subheadingSaves !== "object") return null;
  const clean = String(subheading || "").trim();
  const key = clean ? `h_${clean}` : `h_blank_${fallbackIdx}`;
  return subheadingSaves[key] || null;
}


function hasReportProgress(values) {
  if (!values || typeof values !== "object") return false;
  return Object.values(values).some((value) => {
    if (value == null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "boolean") return true;
    if (typeof value === "object") return Object.keys(value).length > 0;
    return String(value).trim().length > 0;
  });
}


function isFilledValue(value, type = "") {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "boolean") return true;
  if (typeof value === "number") return true;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return String(value).trim().length > 0;
}

function reportItemComplete(item, values, submission) {
  if (isMorpheusField(item)) {
    const result = morpheusResultForField(item, submission);
    return result?.status === "completed" || result?.status === "not_scheduled";
  }
  return isFilledValue(values?.[item?.idx], item?.type);
}

function completionStatsForItems(items = [], values = {}, submission = {}) {
  const total = items.length;
  const done = items.reduce((count, item) => {
    return count + (reportItemComplete(item, values, submission) ? 1 : 0);
  }, 0);
  const pct = total ? Math.round((done / total) * 100) : 0;
  return { done, total, pct };
}

function completionStatsForSection(sec, sub) {
  const fields = fieldsForSubmission(sec, sub);
  const items = fields.map((field, idx) => ({
    ...field,
    idx: Number.isInteger(field?.__sourceIndex) ? field.__sourceIndex : idx,
    type: field?.type || "",
  }));
  return completionStatsForItems(items, sub?.values || {}, sub);
}

function MorpheusReportValue({ result }) {
  if (!result || !["completed", "not_scheduled"].includes(result.status)) return <span>No Morpheus completion recorded for this submission.</span>;
  if (result.status === "not_scheduled") return <span>No Morpheus count scheduled for this day.</span>;
  const completedAt = tsToDate(result.completedAt);
  const timing = result.completionStatus === "late" ? "Late" : result.completionStatus === "on_time" ? "On time" : "";
  return (
    <div style={{ textAlign: "left", lineHeight: 1.65 }}>
      <strong>✓ Count complete in Morpheus</strong>
      {result.cycleName && <div>{result.cycleName}</div>}
      {(result.sections || []).map((section) => (
        <div key={section.sectionId}>
          {section.sectionName || section.sectionId}: {section.counted}/{section.total} products counted
        </div>
      ))}
      <div>Completed by {result.completedBy || "Morpheus"}</div>
      <div>
        {completedAt ? format(completedAt, "dd MMM yyyy, HH:mm") : ""}
        {timing ? ` · ${timing}` : ""}
      </div>
    </div>
  );
}

function formatProgress(stats) {
  if (!stats?.total) return "0/0 • 0%";
  return `${stats.done}/${stats.total} • ${stats.pct}%`;
}

function completionLabel(sub) {
  return sub?.__liveDraft ? "Incomplete" : "Completed";
}

function sectionStatusKind(sub) {
  return sub?.__liveDraft ? "incomplete" : "completed";
}

const num = (x, d = 0) => (typeof x === "number" ? x : Number(x ?? d));
const norm = (s) => String(s || "").trim().toLowerCase();
const domKey = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const isCashUp = (name = "") => /^cash\s*up/i.test(String(name).trim());
const isArchivedRecord = (item) => item?.archived === true || item?.isArchived === true;
const isDeletedRecord = (item) =>
  item?.deleted === true ||
  item?.isDeleted === true ||
  item?.removed === true ||
  item?.deletedAt != null ||
  item?.removedAt != null;
const isVisibleSectionRecord = (item) => !isArchivedRecord(item) && !isDeletedRecord(item);

function formatBreakSummary(shift) {
  const mins = Number(shift?.lastBreakMinutes ?? shift?.breakMinutes ?? 0);
  const lunchTaken = shift?.lunchTaken;
  const reason = String(
    shift?.noLunchReason || shift?.lunchReason || shift?.breakReason || ""
  ).trim();

  if (lunchTaken === false && reason) return `No lunch break taken — ${reason}`;
  if (lunchTaken === false) return "No lunch break taken";
  if (mins > 0) return `Break taken • ${mins} min`;
  if (shift?.breakStatus === "on_break") {
    const started = tsToDate(shift?.breakStartedAt);
    if (started) {
      const live = Math.max(0, Math.floor((Date.now() - started.getTime()) / 60000));
      return `Currently on break • ${live} min`;
    }
    return "Currently on break";
  }
  return "";
}

async function fetchBuilderSections(orgId, storeId) {
  if (!orgId || !storeId) return [];
  try {
    const q1 = query(
      collection(db, "orgs", orgId, "staff_dashboard"),
      where("storeId", "==", storeId)
    );
    const snap = await getDocs(q1);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data(), fields: d.data().fields || [] }))
      .filter((row) => isVisibleSectionRecord(row))
      .sort((a, b) => num(a.order) - num(b.order));
  } catch {
    return [];
  }
}

async function fetchPublishedStandards(orgId, storeId) {
  if (!orgId || !storeId) return [];

  try {
    const snap = await getDocs(collection(db, "orgs", orgId, "neoStandards"));
    return snap.docs
      .map((d) => ({ id: d.id, ...(d.data() || {}) }))
      .filter((row) => {
        const publishState = String(row.publishState || "").toLowerCase();
        const status = String(row.status || "").toLowerCase();
        const isPublished =
          publishState === "published" ||
          status === "published" ||
          row.published === true ||
          row.isPublished === true;

        if (!isPublished) return false;
        if (status === "archived" || row.archived === true || row.isArchived === true) return false;

        const directStoreId = String(row.storeId || "").trim();
        const storeIds = Array.isArray(row.storeIds) ? row.storeIds.map(String) : [];
        const stores = Array.isArray(row.stores)
          ? row.stores.map((item) =>
              String(
                typeof item === "string"
                  ? item
                  : item?.storeId || item?.id || item?.value || ""
              )
            )
          : [];

        // Older published Standards may be org-scoped without an explicit store id.
        return !directStoreId || directStoreId === String(storeId) ||
          storeIds.includes(String(storeId)) || stores.includes(String(storeId));
      });
  } catch {
    return [];
  }
}

function filterReportSectionsByPublishedStandards(sections = [], publishedStandards = []) {
  if (!Array.isArray(publishedStandards) || publishedStandards.length === 0) {
    return sections;
  }

  const clean = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const byId = new Map();
  const byName = new Map();

  publishedStandards.forEach((standard) => {
    [
      standard.staffDashboardSectionId,
      standard.sectionId,
      standard.id,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .forEach((key) => byId.set(key, standard));

    const nameKey = clean(
      standard.name || standard.standardName || standard.sectionName || standard.title
    );
    if (nameKey) byName.set(nameKey, standard);
  });

  return (sections || []).map((section) => {
    if (
      section?.id === CLOCKIN_VIRTUAL_ID ||
      section?.id === "custom_report_fixed"
    ) {
      return section;
    }

    const sectionId = String(section?.id || "").trim();
    const sectionName = clean(
      section?.name || section?.sectionName || section?.title
    );

    const published =
      (sectionId && byId.get(sectionId)) ||
      (section?._match?.builderId && byId.get(section._match.builderId)) ||
      (sectionName && byName.get(sectionName));

    // If this section has no published neoStandard counterpart, preserve it for
    // legacy/custom report compatibility.
    if (!published) return section;

    const duties = Array.isArray(published.duties) ? published.duties : [];
    const byDutyId = new Map();
    const byLabelSubheading = new Map();
    const byLabel = new Map();

    duties.forEach((duty) => {
      if (duty?.active === false || duty?.disabled === true) return;

      [duty?.dutyId, duty?.id]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
        .forEach((key) => byDutyId.set(key, duty));

      const label = clean(duty?.label || duty?.dutyLabel || duty?.name);
      const subheading = clean(duty?.subheading);
      if (label) {
        byLabel.set(label, duty);
        byLabelSubheading.set(`${subheading}::${label}`, duty);
      }
    });

    const fields = (Array.isArray(section?.fields) ? section.fields : [])
      .map((field, sourceIndex) => ({ ...field, __sourceIndex: sourceIndex }))
      .map((field) => {
        const dutyId = String(field?.dutyId || field?.id || "").trim();
        const label = clean(field?.label || field?.dutyLabel || field?.name);
        const subheading = clean(field?.subheading);

        const duty = (
          (dutyId && byDutyId.get(dutyId)) ||
          (label && byLabelSubheading.get(`${subheading}::${label}`)) ||
          (label && byLabel.get(label))
        );
        if (!duty) return null;
        return {
          ...field,
          ...(isMorpheusField(duty) ? {
            type: "morpheus_count", completionSource: "morpheus", dutyId: duty.dutyId,
          } : {}),
        };
      }).filter(Boolean);

    return { ...section, fields };
  });
}


function reportDateKey(value) {
  const date = tsToDate(value);
  return date ? format(date, "yyyy-MM-dd") : "";
}

function reportDutyOperationalDate(item = {}) {
  return (
    item.dueAt ||
    item.scheduledFor ||
    item.dueDate ||
    item.operationDate ||
    item.createdAt ||
    item.updatedAt ||
    null
  );
}

function reportDutyLabel(item = {}) {
  return String(
    item.dutyLabel ||
    item.label ||
    item.name ||
    item.title ||
    "Duty"
  ).trim();
}

function reportDutySectionLabel(item = {}) {
  return String(
    item.sectionName ||
    item.standardName ||
    item.categoryName ||
    item.category ||
    "Standard"
  ).trim();
}

function reportDutyIsResolved(item = {}) {
  const status = String(item.status || "").trim().toLowerCase();
  const decisionStatus = String(
    item?.responseDecision?.status ||
    item?.managerDecision?.status ||
    ""
  ).trim().toLowerCase();

  return Boolean(
    item.completed === true ||
    item.resolved === true ||
    item.closed === true ||
    ["completed", "resolved", "closed", "removed", "cancelled", "canceled"].includes(status) ||
    ["resolved", "closed"].includes(decisionStatus)
  );
}

function reportDutyNeedsDecision(item = {}) {
  const status = String(
    item?.responseDecision?.status ||
    item?.managerDecision?.status ||
    item?.managerEscalation?.status ||
    item.escalationStatus ||
    ""
  ).trim().toLowerCase();

  return Boolean(
    item.needsManagerDecision === true ||
    item.managerDecisionRequired === true ||
    item.requiresManagerDecision === true ||
    ["waiting_for_manager", "needs_decision", "decision_required"].includes(status)
  );
}

function reportDutyIsEscalated(item = {}) {
  const status = String(
    item?.managerEscalation?.status ||
    item.escalationStatus ||
    ""
  ).trim().toLowerCase();

  return Boolean(
    item.escalated === true ||
    ["queued", "sent", "failed", "waiting_for_manager", "escalated"].includes(status)
  );
}

function collectPublishedActiveDutyKeys(standards = []) {
  const keys = new Set();
  const clean = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const add = (value) => {
    const key = clean(value);
    if (key) keys.add(key);
  };

  const visit = (node) => {
    if (!node || typeof node !== "object") return;
    if (node.active === false || node.disabled === true || node.inactive === true) return;

    [
      node.id,
      node.dutyId,
      node.checkId,
      node.key,
      node.name,
      node.label,
      node.dutyLabel,
      node.title,
      node.standardDutyId,
      node.sourceDutyId,
    ].forEach(add);

    Object.entries(node).forEach(([key, value]) => {
      if (["managerEscalation", "dueNotification", "responseDecision"].includes(key)) return;
      if (Array.isArray(value)) value.forEach(visit);
      else if (value && typeof value === "object" && typeof value?.toDate !== "function") visit(value);
    });
  };

  (standards || []).forEach(visit);
  return keys;
}

function carryOverMatchesPublishedDuty(item = {}, activeKeys = new Set()) {
  if (!activeKeys?.size) return true;

  const clean = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  return [
    item.id,
    item.dutyId,
    item.checkId,
    item.key,
    item.dutyLabel,
    item.label,
    item.name,
    item.title,
    item.standardDutyId,
    item.sourceDutyId,
  ]
    .map(clean)
    .filter(Boolean)
    .some((key) => activeKeys.has(key));
}

function carryOverDeadline(item = {}) {
  const direct =
    item.graceUntil ||
    item.graceEndsAt ||
    item.graceDeadline ||
    item.responseGraceUntil ||
    item.responseDeadline ||
    item.deadlineAt ||
    item.expiresAt ||
    item.expireAt ||
    item?.responseRule?.graceUntil ||
    item?.responseRule?.deadlineAt ||
    item?.response?.graceUntil ||
    null;

  const directDate = tsToDate(direct);
  if (directDate && !Number.isNaN(directDate.getTime())) return directDate;

  const due = tsToDate(item.dueAt || item.scheduledFor || item.dueDate || null);
  const graceMinutes = Number(
    item.graceMinutes ??
    item.responseGraceMinutes ??
    item?.responseRule?.graceMinutes ??
    item?.response?.graceMinutes ??
    0
  );

  if (due && !Number.isNaN(due.getTime()) && Number.isFinite(graceMinutes) && graceMinutes > 0) {
    return new Date(due.getTime() + graceMinutes * 60000);
  }

  return null;
}

function reportDutyStillActionable(item = {}, reference = new Date()) {
  // Daily operational duties do NOT become carry-over merely because they were
  // missed yesterday. They only remain operational while an explicitly defined
  // grace/deadline window is still open.
  const deadline = carryOverDeadline(item);
  if (!deadline) return false;
  return deadline.getTime() > reference.getTime();
}

function carryOverAgeLabel(item = {}, reference = new Date()) {
  const when = tsToDate(reportDutyOperationalDate(item));
  if (!when) return "Previous operating day";

  const a = new Date(when);
  a.setHours(0, 0, 0, 0);
  const b = new Date(reference);
  b.setHours(0, 0, 0, 0);
  const days = Math.max(1, Math.round((b.getTime() - a.getTime()) / 86400000));

  return days === 1 ? "Yesterday" : `${days} days ago`;
}

function carryOverDeadlineLabel(item = {}) {
  const deadline = carryOverDeadline(item);
  if (!deadline) return "No active grace window";
  return format(deadline, "EEE HH:mm");
}

function reportTime24(value) {
  const date = value instanceof Date ? value : tsToDate(value);
  return date ? format(date, "HH:mm") : "—";
}


function historicalDutyStatus(item = {}) {
  const status = String(item.status || "").trim().toLowerCase();

  if (
    item.completed === true ||
    ["completed", "resolved", "closed"].includes(status)
  ) {
    return "completed";
  }

  if (
    item.escalated === true ||
    ["escalated", "sent", "failed", "waiting_for_manager"].includes(
      String(item?.managerEscalation?.status || item.escalationStatus || "").toLowerCase()
    )
  ) {
    return "escalated";
  }

  if (
    ["expired", "missed", "overdue"].includes(status) ||
    item.expired === true ||
    item.missed === true
  ) {
    return "missed";
  }

  return "open";
}

function historicalDutyActor(item = {}) {
  return String(
    item.completedBy ||
    item.completedByName ||
    item.staffName ||
    item.ownerName ||
    item.assignedToName ||
    ""
  ).trim();
}

function historicalDutyEventTime(item = {}) {
  return (
    item.completedAt ||
    item.resolvedAt ||
    item.escalatedAt ||
    item.managerNotifiedAt ||
    item.updatedAt ||
    item.dueAt ||
    item.createdAt ||
    null
  );
}

async function fetchHistoricalDutyEvents(orgId, storeId, dateKey, periodKey = "") {
  if (!orgId || !storeId || (!dateKey && !periodKey)) return [];

  try {
    const snap = await getDocs(collection(db, "orgs", orgId, "neoDutyQueue"));

    return snap.docs
      .map((d) => ({ id: d.id, ...(d.data() || {}) }))
      .filter((row) => !row.storeId || String(row.storeId) === String(storeId))
      .filter((row) => reportDateMatchesRange(reportDutyOperationalDate(row), periodKey, dateKey))
      .map((row) => ({
        ...row,
        historyStatus: historicalDutyStatus(row),
        historyActor: historicalDutyActor(row),
        historyEventTime: historicalDutyEventTime(row),
      }))
      .sort((a, b) => {
        const aMs = tsToDate(a.historyEventTime)?.getTime?.() || 0;
        const bMs = tsToDate(b.historyEventTime)?.getTime?.() || 0;
        return bMs - aMs;
      });
  } catch (error) {
    console.error("[Neo Reports MR3] Could not load historical duty events", error);
    return [];
  }
}


function decisionOutcomeFromShift(row = {}) {
  if (row.scheduleVarianceApproved === true) return "approved";
  if (row.scheduleVarianceRejected === true) return "rejected";
  return "";
}

function decisionTimestampFromShift(row = {}) {
  return (
    row.scheduleVarianceDecidedAt ||
    row.scheduleDecisionAt ||
    row.decisionAt ||
    row.updatedAt ||
    row.clockOut ||
    row.clockOutAt ||
    null
  );
}

function decisionActorFromShift(row = {}) {
  return String(
    row.scheduleVarianceDecidedByName ||
    row.scheduleVarianceDecidedBy ||
    row.decisionByName ||
    row.decisionBy ||
    row.updatedByName ||
    row.updatedBy ||
    "Manager"
  ).trim();
}

async function fetchDecisionResponseHistory(orgId, storeId, dateKey, periodKey = "") {
  if (!orgId || !storeId || !dateKey) return [];

  try {
    const [nestedShiftSnap, legacyShiftSnap, dutySnap] = await Promise.all([
      getDocs(collection(db, "orgs", orgId, "stores", storeId, "shifts")),
      getDocs(query(collection(db, "cashup_shifts"), where("orgId", "==", orgId))),
      getDocs(collection(db, "orgs", orgId, "neoDutyQueue")),
    ]);

    const events = [];
    const seenPhysicalDecision = new Set();

    const shiftRows = [
      ...nestedShiftSnap.docs.map((d) => ({
        source: "nested",
        id: d.id,
        forcedStoreId: String(storeId),
        ...(d.data() || {}),
      })),
      ...legacyShiftSnap.docs.map((d) => ({
        source: "legacy",
        id: d.id,
        forcedStoreId: "",
        ...(d.data() || {}),
      })),
    ];

    shiftRows.forEach((row) => {
      const rawStoreId = String(row.forcedStoreId || row.storeId || row.store || "").trim();
      if (rawStoreId && rawStoreId !== String(storeId)) return;

      const outcome = decisionOutcomeFromShift(row);
      if (!outcome) return;

      const inSec = reportFirestoreSeconds(
        row.clockIn || row.clockInAt || row.in || row.inAt
      );
      const outSec = reportFirestoreSeconds(
        row.clockOut || row.clockOutAt || row.out || row.outAt
      );
      if (!inSec) return;

      const operatingDateValue = new Date(inSec * 1000);
      if (!reportDateMatchesRange(operatingDateValue, periodKey, dateKey)) return;

      const staffId = String(
        row.staffId || row.userId || row.userUid || row.uid || row.employeeId || ""
      ).trim();
      const staffName = String(
        row.name ||
        row.staffName ||
        row.staffNameSnapshot ||
        row.employeeName ||
        row.displayName ||
        "Staff member"
      ).trim();

      // Same physical-shift identity used elsewhere in Reports/Admin parity.
      const physicalKey = `${staffId || staffName.toLowerCase()}|${inSec}|${storeId}|${outcome}`;
      if (seenPhysicalDecision.has(physicalKey)) return;
      seenPhysicalDecision.add(physicalKey);

      const decidedAt = tsToDate(decisionTimestampFromShift(row)) || (outSec ? new Date(outSec * 1000) : new Date(inSec * 1000));
      const approvedLabel = outcome === "approved" ? "Approved" : "Rejected";

      events.push({
        id: `schedule-${physicalKey}`,
        kind: "schedule",
        outcome,
        title: `${approvedLabel} attendance exception`,
        subject: staffName,
        actor: decisionActorFromShift(row),
        roleName: String(row.roleName || row.role || "").trim(),
        detail:
          outcome === "approved"
            ? "Attendance exception approved and synchronized with Labor/Schedule Intelligence."
            : "Attendance exception rejected and synchronized with Labor/Schedule Intelligence.",
        time: decidedAt,
      });
    });

    dutySnap.docs.forEach((docSnap) => {
      const row = { id: docSnap.id, ...(docSnap.data() || {}) };
      if (row.storeId && String(row.storeId) !== String(storeId)) return;
      if (!reportDateMatchesRange(reportDutyOperationalDate(row), periodKey, dateKey)) return;

      const escalationStatus = String(
        row?.managerEscalation?.status ||
        row.escalationStatus ||
        row.managerNotificationStatus ||
        ""
      ).toLowerCase();

      const escalationTime =
        row.managerNotifiedAt ||
        row.escalatedAt ||
        row?.managerEscalation?.sentAt ||
        row?.managerEscalation?.updatedAt ||
        null;

      if (
        row.escalated === true ||
        ["sent", "escalated", "waiting_for_manager", "failed"].includes(escalationStatus)
      ) {
        events.push({
          id: `response-${row.id}`,
          kind: "response",
          outcome: escalationStatus === "failed" ? "failed" : "escalated",
          title: "Manager response escalation",
          subject: reportDutyLabel(row),
          actor: String(
            row.managerName ||
            row.managerNotifiedName ||
            row.escalatedToName ||
            "Neo"
          ).trim(),
          roleName: reportDutySectionLabel(row),
          detail:
            escalationStatus === "failed"
              ? "Neo attempted a manager escalation but the notification failed."
              : "Neo escalated this duty for management attention.",
          time: tsToDate(escalationTime) || tsToDate(historicalDutyEventTime(row)),
        });
      }
    });

    return events.sort((a, b) => {
      const aMs = a.time?.getTime?.() || 0;
      const bMs = b.time?.getTime?.() || 0;
      return bMs - aMs;
    });
  } catch (error) {
    console.error("[Neo Reports MR4] Could not load decision/response history", error);
    return [];
  }
}

async function fetchCarryOverDuties(orgId, storeId, publishedStandards = []) {
  if (!orgId || !storeId) return [];

  try {
    const snap = await getDocs(collection(db, "orgs", orgId, "neoDutyQueue"));
    const now = new Date();
    const today = format(now, "yyyy-MM-dd");
    const activeDutyKeys = collectPublishedActiveDutyKeys(publishedStandards);

    return snap.docs
      .map((d) => ({ id: d.id, ...(d.data() || {}) }))
      .filter((row) => !row.storeId || String(row.storeId) === String(storeId))
      .filter((row) => !reportDutyIsResolved(row))
      .filter((row) => {
        const dateKey = reportDateKey(reportDutyOperationalDate(row));
        return dateKey && dateKey < today;
      })
      .filter((row) => carryOverMatchesPublishedDuty(row, activeDutyKeys))
      .filter((row) => reportDutyStillActionable(row, now))
      .sort((a, b) => {
        const aDeadline = carryOverDeadline(a)?.getTime?.() || Number.MAX_SAFE_INTEGER;
        const bDeadline = carryOverDeadline(b)?.getTime?.() || Number.MAX_SAFE_INTEGER;
        return aDeadline - bDeadline;
      });
  } catch (error) {
    console.error("[Neo Reports MR2] Could not load actionable carry-over duties", error);
    return [];
  }
}

function scheduleDecisionLabel(row = {}) {
  return String(
    row.intelligenceLabel ||
    row.neoIntelligence ||
    row.exceptionLabel ||
    row.exceptionType ||
    row.attendanceException ||
    row.overtimeLabel ||
    "Attendance exception requires a manager decision"
  ).trim();
}

function reportActualAttendanceLabel(row = {}) {
  const inSec = Number(row.clockInSec || 0);
  const outSec = Number(row.clockOutSec || 0);

  const timeLabel = (seconds) => {
    if (!seconds) return "";
    return format(new Date(seconds * 1000), "HH:mm");
  };

  const inLabel = timeLabel(inSec);
  const outLabel = timeLabel(outSec);

  if (!inLabel) return "Not clocked in";
  return `${inLabel} → ${outLabel || "On shift"}`;
}

function reportFirestoreSeconds(value) {
  if (!value) return null;
  if (typeof value?.seconds === "number") return Number(value.seconds);
  if (typeof value?.toDate === "function") return Math.floor(value.toDate().getTime() / 1000);
  if (typeof value === "number") {
    return value > 100000000000 ? Math.floor(value / 1000) : Math.floor(value);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : Math.floor(parsed.getTime() / 1000);
}

function reportScheduleWindow(entry = {}) {
  const publishedShift = String(entry.publishedShift || entry.shift || "").trim();
  const parsed = publishedShift.match(/^(\d{2}:\d{2})-(\d{2}:\d{2})$/);
  return {
    startTime: entry.startTime || parsed?.[1] || "",
    endTime: entry.endTime || parsed?.[2] || "",
    label:
      publishedShift ||
      (entry.startTime && entry.endTime
        ? `${entry.startTime}-${entry.endTime}`
        : "—"),
  };
}

function reportScheduleTimeSeconds(dateKey, time) {
  const match = String(time || "").match(/^(\d{2}):(\d{2})$/);
  if (!dateKey || !match) return 0;
  const [year, month, day] = String(dateKey).split("-").map(Number);
  return Math.floor(
    new Date(
      year,
      month - 1,
      day,
      Number(match[1]),
      Number(match[2]),
      0,
      0
    ).getTime() / 1000
  );
}

function reportShiftDateKey(shift = {}) {
  const seconds = reportFirestoreSeconds(
    shift.clockIn || shift.clockInAt || shift.in || shift.inAt
  );
  return seconds ? format(new Date(seconds * 1000), "yyyy-MM-dd") : "";
}

function reportShiftIdentity(shift = {}) {
  return {
    staffId: String(
      shift.staffId ||
      shift.userId ||
      shift.userUid ||
      shift.uid ||
      shift.employeeId ||
      ""
    ).trim(),
    staffName: String(
      shift.name ||
      shift.staffName ||
      shift.staffNameSnapshot ||
      shift.employeeName ||
      shift.displayName ||
      ""
    ).trim(),
  };
}

function pendingScheduleDecisionFromMatch(entry = {}, shift = null) {
  if (!shift) return null;
  if (
    shift.scheduleVarianceApproved === true ||
    shift.scheduleVarianceRejected === true
  ) {
    return null;
  }

  const dateKey = String(entry.date || reportShiftDateKey(shift) || "");
  if (!dateKey) return null;

  const inSec = reportFirestoreSeconds(
    shift.clockIn || shift.clockInAt || shift.in || shift.inAt
  );
  const outSec = reportFirestoreSeconds(
    shift.clockOut || shift.clockOutAt || shift.out || shift.outAt
  );

  // Neo Admin only exposes Approve / Reject once the shift has finished.
  if (!inSec || !outSec) return null;

  const window = reportScheduleWindow(entry);
  const isOff =
    entry.off === true ||
    String(entry.publishedShift || "").trim().toUpperCase() === "OFF";

  if (isOff) {
    return {
      type: "unscheduled",
      label: "Worked on an OFF day · approval required",
      minutes: Math.max(1, Math.round((outSec - inSec) / 60)),
    };
  }

  const startSec = reportScheduleTimeSeconds(dateKey, window.startTime);
  let endSec = reportScheduleTimeSeconds(dateKey, window.endTime);
  if (startSec && endSec && endSec <= startSec) endSec += 86400;

  const earlyMinutes =
    startSec && inSec < startSec ? Math.max(1, Math.round((startSec - inSec) / 60)) : 0;
  const overtimeMinutes =
    endSec && outSec > endSec ? Math.max(1, Math.round((outSec - endSec) / 60)) : 0;

  // Match Neo Admin / Manager Schedule Intelligence semantics:
  // early start and work beyond the scheduled end require approval.
  if (overtimeMinutes > 0) {
    return {
      type: "overtime",
      label: `${formatDurationMinutes(overtimeMinutes)} overtime · approval required`,
      minutes: overtimeMinutes,
    };
  }

  if (earlyMinutes > 0) {
    return {
      type: "overtime",
      label: `${formatDurationMinutes(earlyMinutes)} early overtime · approval required`,
      minutes: earlyMinutes,
    };
  }

  return null;
}

async function fetchPendingScheduleDecisions(orgId, storeId) {
  if (!orgId || !storeId) return [];

  try {
    // MR2.7 — use the same physical-shift identity logic as Neo Admin
    // Schedule Intelligence. A physical attendance shift can exist in both
    // nested store shifts and legacy cashup_shifts. We reconcile those copies
    // before deciding whether an exception is still pending.
    const [scheduleSnap, nestedShiftSnap, legacyShiftSnap, staffSnap] = await Promise.all([
      getDocs(collection(db, "orgs", orgId, "neo_schedule_entries")),
      getDocs(collection(db, "orgs", orgId, "stores", storeId, "shifts")),
      getDocs(query(collection(db, "cashup_shifts"), where("orgId", "==", orgId))),
      getDocs(
        query(
          collection(db, "orgs", orgId, "cashup_users"),
          where("storeId", "==", storeId)
        )
      ),
    ]);

    const activeStaff = staffSnap.docs
      .map((d) => ({ id: d.id, ...(d.data() || {}) }))
      .filter((row) => row.archived !== true && row.isArchived !== true)
      .filter((row) => row.active !== false && row.isActive !== false);

    const activeStaffIds = new Set();
    const activeStaffNames = new Set();

    activeStaff.forEach((row) => {
      [
        row.id,
        row.staffId,
        row.userId,
        row.userUid,
        row.uid,
        row.employeeId,
      ]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
        .forEach((value) => activeStaffIds.add(value));

      const name = String(
        row.name || row.staffName || row.displayName || row.employeeName || ""
      )
        .trim()
        .toLowerCase();

      if (name) activeStaffNames.add(name);
    });

    const isCurrentStaff = (staffId, staffName) => {
      const id = String(staffId || "").trim();
      const name = String(staffName || "").trim().toLowerCase();
      return Boolean(
        (id && activeStaffIds.has(id)) ||
        (name && activeStaffNames.has(name))
      );
    };

    const scheduleMap = new Map();

    scheduleSnap.docs.forEach((scheduleDoc) => {
      const data = scheduleDoc.data() || {};
      const dateKey = String(data.date || "");
      const rowStoreId = String(data.storeId || "");

      if (!dateKey || rowStoreId !== String(storeId) || data.published !== true) return;

      const publishedShift = String(
        (
          data.varianceApproved === true && data.originalPublishedShift
            ? data.originalPublishedShift
            : data.publishedShift
        ) ||
        data.shift ||
        ""
      );

      if (!/^\d{2}:\d{2}-\d{2}:\d{2}$/.test(publishedShift)) return;

      const staffId = String(data.staffId || "").trim();
      const staffName = String(data.staffName || data.name || "").trim().toLowerCase();

      if (staffId) scheduleMap.set(`id:${staffId}|${dateKey}`, publishedShift);
      if (staffName) scheduleMap.set(`name:${staffName}|${dateKey}`, publishedShift);
    });

    const rawRows = [
      ...nestedShiftSnap.docs.map((shiftDoc) => ({
        source: "nested",
        docId: shiftDoc.id,
        forcedStoreId: String(storeId),
        data: shiftDoc.data() || {},
      })),
      ...legacyShiftSnap.docs.map((shiftDoc) => ({
        source: "legacy",
        docId: shiftDoc.id,
        forcedStoreId: "",
        data: shiftDoc.data() || {},
      })),
    ];

    const normalizeStoreId = (rawStore) => {
      const value = String(rawStore || "").trim();
      if (!value) return "";
      return value === String(storeId) ? String(storeId) : value;
    };

    const shiftAliases = ({ data = {}, raw = {}, clockInSec = 0, normalizedStoreId = "" }) => {
      const staffId = String(
        data.staffId || data.userId || data.userUid || data.uid || data.employeeId || ""
      ).trim();

      const staffName = String(
        data.name ||
        data.staffName ||
        data.staffNameSnapshot ||
        data.employeeName ||
        data.displayName ||
        ""
      ).trim().toLowerCase();

      const aliases = [];
      if (staffId) aliases.push(`id:${staffId}|${clockInSec}|${normalizedStoreId}`);
      if (staffName) aliases.push(`name:${staffName}|${clockInSec}|${normalizedStoreId}`);
      return aliases;
    };

    // Exactly like Neo Admin: if either physical copy has already been
    // approved/rejected, all aliases of that physical shift are resolved.
    const resolvedShiftAliases = new Set();

    rawRows.forEach((raw) => {
      const data = raw.data || {};
      const clockInSec =
        reportFirestoreSeconds(data.clockIn) ??
        reportFirestoreSeconds(data.clockInAt) ??
        reportFirestoreSeconds(data.in) ??
        reportFirestoreSeconds(data.inAt);

      if (!clockInSec) return;

      const rawStore = String(raw.forcedStoreId || data.storeId || data.store || "").trim();
      const normalizedStoreId = normalizeStoreId(rawStore);
      if (normalizedStoreId !== String(storeId)) return;

      if (
        data.scheduleVarianceApproved === true ||
        data.scheduleVarianceRejected === true
      ) {
        shiftAliases({
          data,
          raw,
          clockInSec,
          normalizedStoreId,
        }).forEach((key) => resolvedShiftAliases.add(key));
      }
    });

    const physicalShiftMap = new Map();

    rawRows.forEach((raw) => {
      const data = raw.data || {};

      const clockInSec =
        reportFirestoreSeconds(data.clockIn) ??
        reportFirestoreSeconds(data.clockInAt) ??
        reportFirestoreSeconds(data.in) ??
        reportFirestoreSeconds(data.inAt);

      const clockOutSec =
        reportFirestoreSeconds(data.clockOut) ??
        reportFirestoreSeconds(data.clockOutAt) ??
        reportFirestoreSeconds(data.out) ??
        reportFirestoreSeconds(data.outAt);

      if (!clockInSec || !clockOutSec || clockOutSec <= clockInSec) return;
      if (data.archived === true || data.isArchived === true || data.payrollZeroHours === true) return;

      const rawStore = String(raw.forcedStoreId || data.storeId || data.store || "").trim();
      const normalizedStoreId = normalizeStoreId(rawStore);
      if (normalizedStoreId !== String(storeId)) return;

      const staffId = String(
        data.staffId || data.userId || data.userUid || data.uid || data.employeeId || ""
      ).trim();

      const staffName = String(
        data.name ||
        data.staffName ||
        data.staffNameSnapshot ||
        data.employeeName ||
        data.displayName ||
        ""
      ).trim();

      if (!isCurrentStaff(staffId, staffName)) return;

      const aliases = shiftAliases({
        data,
        raw,
        clockInSec,
        normalizedStoreId,
      });

      if (
        data.scheduleVarianceApproved === true ||
        data.scheduleVarianceRejected === true ||
        aliases.some((key) => resolvedShiftAliases.has(key))
      ) {
        return;
      }

      const clockInDate = new Date(clockInSec * 1000);
      const dateKey = format(clockInDate, "yyyy-MM-dd");

      const publishedShift =
        (staffId ? scheduleMap.get(`id:${staffId}|${dateKey}`) : "") ||
        (staffName ? scheduleMap.get(`name:${staffName.toLowerCase()}|${dateKey}`) : "") ||
        "";

      const match = publishedShift.match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);

      let exceptionType = "unscheduled";
      let pendingMinutes = Math.max(1, Math.round((clockOutSec - clockInSec) / 60));
      let intelligenceLabel = "Worked without a published shift · approval required";
      let scheduledText = "Not scheduled";

      if (match) {
        const scheduledStart = new Date(
          clockInDate.getFullYear(),
          clockInDate.getMonth(),
          clockInDate.getDate(),
          Number(match[1]),
          Number(match[2]),
          0,
          0
        );

        const scheduledEnd = new Date(
          clockInDate.getFullYear(),
          clockInDate.getMonth(),
          clockInDate.getDate(),
          Number(match[3]),
          Number(match[4]),
          0,
          0
        );

        if (scheduledEnd <= scheduledStart) {
          scheduledEnd.setDate(scheduledEnd.getDate() + 1);
        }

        const startSec = Math.floor(scheduledStart.getTime() / 1000);
        const endSec = Math.floor(scheduledEnd.getTime() / 1000);

        // This is deliberately the same boundary calculation used by
        // ScheduleIntelligenceTab historical exceptions.
        const earlySeconds = Math.max(
          0,
          Math.min(clockOutSec, startSec) - clockInSec
        );

        const lateSeconds = Math.max(
          0,
          clockOutSec - Math.max(clockInSec, endSec)
        );

        const outsideSeconds = earlySeconds + lateSeconds;
        if (outsideSeconds <= 0) return;

        pendingMinutes = Math.max(1, Math.round(outsideSeconds / 60));
        exceptionType = "overtime";
        intelligenceLabel = `${formatDurationMinutes(pendingMinutes)} overtime · approval required`;
        scheduledText = publishedShift;
      }

      const roleName = String(data.roleName || data.role || "").trim();

      const item = {
        id: `historical-${exceptionType}-${raw.source}-${raw.docId}`,
        shiftId: `${raw.source}:${raw.docId}`,
        rawShiftId: raw.docId,
        shiftSource: raw.source,
        staffId,
        staffName: staffName || "Staff member",
        roleName,
        storeId: String(storeId),
        date: dateKey,
        exceptionType,
        intelligenceLabel,
        minutes: pendingMinutes,
        scheduledText,
        clockInSec,
        clockOutSec,
      };

      // Neo Admin's physical identity is staff + clock-in + store.
      // Prefer nested over legacy when both represent the same physical shift.
      const physicalKey = `${
        staffId || staffName.toLowerCase()
      }|${clockInSec}|${String(storeId)}`;

      const existing = physicalShiftMap.get(physicalKey);

      if (!existing || raw.source === "nested") {
        physicalShiftMap.set(physicalKey, item);
      }
    });

    return Array.from(physicalShiftMap.values()).sort((a, b) => {
      if (Number(a.clockInSec || 0) !== Number(b.clockInSec || 0)) {
        return Number(b.clockInSec || 0) - Number(a.clockInSec || 0);
      }
      return String(a.staffName || "").localeCompare(String(b.staffName || ""));
    });
  } catch (error) {
    console.error("[Neo Reports MR2.7] Could not load Admin-parity schedule exceptions", error);
    return [];
  }
}

async function fetchContentSections(orgId) {
  if (!orgId) return [];
  try {
    const snap = await getDocs(collection(db, "orgs", orgId, "dashContent"));
    const secs = snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      fields: d.data().fields || [],
    })).filter((row) => isVisibleSectionRecord(row));
    if (secs.length) return secs;
  } catch {}

  try {
    const q1 = query(collection(db, "dashContent"), where("orgId", "==", orgId));
    const snap = await getDocs(q1);
    return snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      fields: d.data().fields || [],
    })).filter((row) => isVisibleSectionRecord(row));
  } catch {
    return [];
  }
}

function buildReportSections(builder, content) {
  const byNameContent = new Map(content.map((c) => [norm(c.name), c]));
  const fromBuilder = builder.map((b) => {
    const c = byNameContent.get(norm(b.name));
    return {
      id: c?.id || b.id,
      name: c?.name || b.name,
      fields: (c?.fields && c.fields.length ? c.fields : b.fields) || [],
      _match: { preferId: c?.id || null, builderId: b.id, nameKey: norm(b.name) },
      order: num(b.order),
    };
  });

  const base = fromBuilder.length
    ? fromBuilder
    : content.map((c) => ({
        id: c.id,
        name: c.name,
        fields: c.fields || [],
        _match: { preferId: c.id, nameKey: norm(c.name) },
        order: num(c.order),
      }));

  const seen = new Set();
  const unique = base.filter((s) => {
    const k = norm(s.name);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  const cashIdx = unique.findIndex((s) => isCashUp(s.name));
  let ordered = unique;
  if (cashIdx > -1) {
    const [cash] = ordered.splice(cashIdx, 1);
    ordered = [cash, ...ordered];
  }

  return [{ id: CLOCKIN_VIRTUAL_ID, name: "Clocked Hours", fields: [] }, ...ordered, { id: "custom_report_fixed", name: "Custom Report", fields: [] }];
}

function getStyles(themeMode = "light") {
  const dark = themeMode === "dark";

  return {
    page: {
      maxWidth: "100%",
      margin: 0,
      padding: 0,
      background: "transparent",
      color: dark ? "#f4f7fb" : "#0f2747",
    },
    surface: {
      display: "flex",
      flexDirection: "column",
      gap: 16,
    },
    hero: {
      position: "relative",
      overflow: "hidden",
      borderRadius: 28,
      padding: "28px 28px 24px",
      background: dark
        ? "linear-gradient(135deg, rgba(4,18,43,0.98) 0%, rgba(3,17,42,0.98) 46%, rgba(2,10,26,0.98) 100%)"
        : "linear-gradient(135deg, rgba(245,248,253,0.98) 0%, rgba(239,244,251,0.98) 45%, rgba(232,239,249,0.98) 100%)",
      border: dark
        ? "1px solid rgba(67, 113, 181, 0.28)"
        : "1px solid rgba(150, 174, 205, 0.28)",
      boxShadow: dark
        ? "0 24px 60px rgba(0,0,0,0.34)"
        : "0 20px 45px rgba(24, 55, 96, 0.08)",
    },
    heroGlow: {
      position: "absolute",
      inset: "auto auto -90px 30%",
      width: 260,
      height: 260,
      borderRadius: "50%",
      background: dark
        ? "radial-gradient(circle, rgba(65,147,255,0.22) 0%, rgba(65,147,255,0.08) 38%, rgba(65,147,255,0) 72%)"
        : "radial-gradient(circle, rgba(72,144,255,0.16) 0%, rgba(72,144,255,0.06) 38%, rgba(72,144,255,0) 72%)",
      pointerEvents: "none",
      filter: "blur(10px)",
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 14px",
      borderRadius: 999,
      border: dark
        ? "1px solid rgba(67, 113, 181, 0.35)"
        : "1px solid rgba(150, 174, 205, 0.4)",
      background: dark ? "rgba(10, 29, 61, 0.72)" : "rgba(255,255,255,0.66)",
      color: dark ? "#74b3ff" : "#2f6ff2",
      fontSize: 12,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.14em",
      width: "fit-content",
      marginBottom: 18,
      position: "relative",
      zIndex: 1,
    },
    heroRow: {
      position: "relative",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 18,
      flexWrap: "wrap",
      zIndex: 1,
    },
    heroLeft: {
      minWidth: 260,
      flex: "1 1 420px",
    },
    heroRight: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 8,
      minWidth: 180,
      marginLeft: "auto",
    },
    title: {
      margin: 0,
      fontSize: "clamp(2rem, 4vw, 3rem)",
      lineHeight: 1.04,
      fontWeight: 900,
      color: dark ? "#f4f7fb" : "#112c53",
    },
    titleAccent: {
      color: "#4f9cff",
      marginLeft: 10,
    },
    subtitle: {
      marginTop: 10,
      color: dark ? "rgba(223,233,248,0.78)" : "#607897",
      fontSize: 16,
      maxWidth: 560,
    },
    liveTime: {
      fontSize: "clamp(2rem, 4vw, 3.3rem)",
      lineHeight: 0.95,
      fontWeight: 900,
      color: dark ? "#f4f7fb" : "#12335f",
      letterSpacing: "-0.04em",
    },
    liveSub: {
      color: dark ? "rgba(223,233,248,0.78)" : "#607897",
      fontSize: 14,
      textAlign: "right",
    },
    onlinePill: {
      display: "inline-flex",
      alignItems: "center",
      padding: "8px 14px",
      borderRadius: 999,
      background: dark ? "rgba(25, 163, 116, 0.16)" : "rgba(63, 194, 135, 0.16)",
      border: "1px solid rgba(63, 194, 135, 0.25)",
      color: "#17a56e",
      fontWeight: 800,
      fontSize: 13,
    },
    filterCard: {
      borderRadius: 24,
      padding: "18px 20px",
      background: dark
        ? "linear-gradient(180deg, rgba(18,40,68,0.98) 0%, rgba(12,30,54,0.98) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,251,255,0.98) 100%)",
      border: dark
        ? "1px solid rgba(123, 186, 255, 0.22)"
        : "1px solid rgba(150, 174, 205, 0.22)",
      boxShadow: dark
        ? "0 22px 42px rgba(0,0,0,0.26), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(91,160,255,0.04)"
        : "0 16px 36px rgba(24, 55, 96, 0.08)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 16,
      flexWrap: "wrap",
    },
    filterLabel: {
      fontSize: 12,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.12em",
      color: dark ? "#89aedf" : "#6d83a2",
      marginBottom: 8,
    },
    filterRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
    },
    inputDate: {
      border: dark
        ? "1px solid rgba(67, 113, 181, 0.24)"
        : "1px solid rgba(150, 174, 205, 0.3)",
      borderRadius: 12,
      padding: "12px 14px",
      fontSize: 14,
      color: dark ? "#e6eef8" : "#12335f",
      background: dark ? "rgba(10, 29, 61, 0.76)" : "#fff",
    },
    search: {
      background: "linear-gradient(135deg, rgba(22,163,116,0.96) 0%, rgba(49,198,145,0.96) 100%)",
      color: "#fff",
      border: "none",
      borderRadius: 14,
      padding: "12px 18px",
      cursor: "pointer",
      fontWeight: 800,
      boxShadow: "0 12px 24px rgba(49,198,145,0.16)",
    },
    sections: { display: "flex", flexDirection: "column", gap: 14 },
    sectionBtn: {
      textAlign: "left",
      padding: "18px 20px",
      background: dark
        ? "linear-gradient(180deg, rgba(18,40,68,0.98) 0%, rgba(12,30,54,0.98) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,251,255,0.98) 100%)",
      border: dark
        ? "1px solid rgba(123, 186, 255, 0.22)"
        : "1px solid rgba(150, 174, 205, 0.22)",
      borderRadius: 20,
      fontWeight: 900,
      fontSize: 18,
      color: dark ? "#f4f7fb" : "#112c53",
      cursor: "pointer",
      boxShadow: dark
        ? "0 26px 52px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(91,160,255,0.04)"
        : "0 16px 36px rgba(24, 55, 96, 0.08)",
    },
    sectionActive: {
      background: dark
        ? "linear-gradient(180deg, rgba(9,40,33,0.96) 0%, rgba(6,31,25,0.96) 100%)"
        : "linear-gradient(180deg, rgba(232,249,240,0.98) 0%, rgba(241,252,246,0.98) 100%)",
      border: dark
        ? "1px solid rgba(63, 194, 135, 0.22)"
        : "1px solid rgba(63, 194, 135, 0.22)",
    },
    contentBox: {
      background: dark
        ? "linear-gradient(180deg, rgba(18,40,68,0.98) 0%, rgba(12,30,54,0.98) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,250,254,0.98) 100%)",
      padding: 18,
      borderRadius: 24,
      boxShadow: dark
        ? "0 26px 52px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(91,160,255,0.04)"
        : "0 18px 40px rgba(24, 55, 96, 0.08)",
      border: dark
        ? "1px solid rgba(67, 113, 181, 0.2)"
        : "1px solid rgba(150, 174, 205, 0.2)",
      overflow: "hidden",
    },
    tableWrap: { overflowX: "auto" },
    clockTable: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      tableLayout: "fixed",
      fontSize: 14,
    },
    thLeft: {
      width: "25%",
      borderBottom: dark ? "1px solid rgba(67,113,181,0.14)" : "1px solid #e6eef6",
      background: dark ? "linear-gradient(180deg, rgba(22,45,77,0.98), rgba(14,31,54,0.98))" : "#f2f6fb",
      textAlign: "left",
      padding: "12px 14px",
      borderTopLeftRadius: 10,
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      color: dark ? "#8db4e6" : "#6d83a2",
    },
    thMid: {
      width: "20%",
      borderBottom: dark ? "1px solid rgba(67,113,181,0.14)" : "1px solid #e6eef6",
      background: dark ? "linear-gradient(180deg, rgba(22,45,77,0.98), rgba(14,31,54,0.98))" : "#f2f6fb",
      textAlign: "center",
      padding: "12px 14px",
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      color: dark ? "#8db4e6" : "#6d83a2",
    },
    thRight: {
      width: "35%",
      borderBottom: dark ? "1px solid rgba(67,113,181,0.14)" : "1px solid #e6eef6",
      background: dark ? "linear-gradient(180deg, rgba(22,45,77,0.98), rgba(14,31,54,0.98))" : "#f2f6fb",
      textAlign: "right",
      padding: "12px 14px",
      borderTopRightRadius: 10,
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      color: dark ? "#8db4e6" : "#6d83a2",
    },
    tdLeft: {
      padding: "14px",
      borderBottom: dark ? "1px solid rgba(67,113,181,0.08)" : "1px solid #eef3f8",
      verticalAlign: "top",
      color: dark ? "#eff5ff" : "#173960",
    },
    tdMid: {
      padding: "14px",
      textAlign: "center",
      borderBottom: dark ? "1px solid rgba(67,113,181,0.08)" : "1px solid #eef3f8",
      verticalAlign: "top",
      color: dark ? "#eff5ff" : "#173960",
    },
    photoThumbLink: { display: "inline-flex", alignItems: "center", justifyContent: "center" },
    photoThumb: { width: 46, height: 46, objectFit: "cover", borderRadius: 10, border: dark ? "1px solid rgba(67,113,181,0.28)" : "1px solid rgba(150,174,205,0.28)", boxShadow: dark ? "0 6px 16px rgba(0,0,0,0.24)" : "0 6px 14px rgba(24,55,96,0.12)" },
    tdRight: {
      padding: "14px",
      textAlign: "right",
      borderBottom: dark ? "1px solid rgba(67,113,181,0.08)" : "1px solid #eef3f8",
      verticalAlign: "top",
      color: dark ? "#eff5ff" : "#173960",
    },
    durationCell: { display: "flex", flexDirection: "column", gap: 4, minWidth: 160 },
    durationMain: { fontWeight: 800, color: dark ? "#eff5ff" : "#173960" },
    durationMeta: { fontSize: 12, color: dark ? "#9eb6d0" : "#6d83a2", lineHeight: 1.35 },
    durationMetaWarn: { fontSize: 12, color: dark ? "#ffcf6b" : "#c77800", lineHeight: 1.35, fontWeight: 700 },
    totalRowSpacer: {
      padding: "14px",
      borderBottom: dark ? "1px solid rgba(67,113,181,0.08)" : "1px solid #eef3f8",
    },
    totalRowDuration: {
      padding: "14px",
      textAlign: "right",
      borderBottom: dark ? "1px solid rgba(67,113,181,0.08)" : "1px solid #eef3f8",
      color: dark ? "#eff5ff" : "#173960",
    },
    totalDurationWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      width: "100%",
    },
    totalDurationLabel: {
      fontWeight: 800,
      color: dark ? "#eff5ff" : "#173960",
    },
    totalDurationValue: {
      fontWeight: 900,
      color: dark ? "#eff5ff" : "#173960",
    },
    card: { marginBottom: 16 },
    headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" },
    headerLeft: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "6px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 900,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
    },
    progressPill: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "6px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 900,
      letterSpacing: "0.04em",
      whiteSpace: "nowrap",
      color: dark ? "#7fc3ff" : "#1e63c7",
      background: dark ? "rgba(63, 158, 255, 0.13)" : "rgba(63, 158, 255, 0.12)",
      border: dark ? "1px solid rgba(63, 158, 255, 0.24)" : "1px solid rgba(63, 158, 255, 0.2)",
    },
    subheadInner: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
    },
    statusCompleted: {
      background: dark ? "rgba(25, 163, 116, 0.16)" : "rgba(63, 194, 135, 0.16)",
      border: "1px solid rgba(63, 194, 135, 0.25)",
      color: "#17a56e",
    },
    statusIncomplete: {
      background: dark ? "rgba(255, 183, 77, 0.13)" : "rgba(255, 183, 77, 0.18)",
      border: "1px solid rgba(255, 183, 77, 0.28)",
      color: dark ? "#ffd18c" : "#a86600",
    },
    liveMeta: {
      color: dark ? "#9eb6d0" : "#607897",
      fontSize: 12,
      fontWeight: 700,
    },
    tsBtn: {
      background: dark ? "linear-gradient(180deg, rgba(22,45,77,0.98), rgba(14,31,54,0.98))" : "#e9eef6",
      border: dark ? "1px solid rgba(67,113,181,0.24)" : "1px solid #d7e2f0",
      padding: "8px 12px",
      borderRadius: 10,
      cursor: "pointer",
      color: dark ? "#dbe7fb" : "#12335f",
      fontWeight: 700,
    },
    innerBox: {
      background: dark ? "rgba(18,40,68,0.62)" : "#fafcff",
      borderRadius: 14,
      padding: 16,
      border: dark ? "1px solid rgba(67,113,181,0.18)" : "1px solid #edf3f9",
    },
    rowMeta: { display: "flex", gap: 8, alignItems: "baseline", marginBottom: 8 },
    subTable: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
    subheadCell: {
      background: dark ? "rgba(11,34,68,0.72)" : "#eaf2fb",
      color: dark ? "#dbe7fb" : "#334155",
      fontWeight: 700,
      padding: "8px 12px",
      borderTop: dark ? "1px solid rgba(67,113,181,0.14)" : "1px solid #e1e7ef",
      borderBottom: dark ? "1px solid rgba(67,113,181,0.14)" : "1px solid #e1e7ef",
    },
    subMetaCell: {
      background: dark ? "rgba(18,40,68,0.62)" : "#f8fafc",
      color: dark ? "#9eb6d0" : "#475569",
      fontSize: 12,
      padding: "8px 12px",
      borderBottom: dark ? "1px solid rgba(67,113,181,0.12)" : "1px solid #eef2f7",
    },
    fileList: { display: "flex", gap: 10, flexWrap: "wrap" },
    fileLink: { display: "inline-block", textDecoration: "none" },
    thumb: { width: 140, height: 140, objectFit: "cover", borderRadius: 8, border: dark ? "1px solid rgba(67,113,181,0.24)" : "1px solid #e5e7eb" },
    fileChip: {
      display: "inline-block",
      background: dark ? "linear-gradient(180deg, rgba(22,45,77,0.98), rgba(14,31,54,0.98))" : "#e9eef6",
      border: dark ? "1px solid rgba(67,113,181,0.24)" : "1px solid #d7e2f0",
      padding: "6px 10px",
      borderRadius: 8,
      fontSize: 13,
      color: dark ? "#dbe7fb" : "#1f2937",
    },
    small: { color: dark ? "#9eb6d0" : "#666", fontSize: 12 },
    bold: { color: dark ? "#eff5ff" : "#000", fontWeight: "bold" },
    empty: { textAlign: "center", color: dark ? "#9eb6d0" : "#777", margin: 20 },
    intelligenceNav: {
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
      padding: 8,
      borderRadius: 20,
      width: "fit-content",
      background: dark ? "rgba(7,22,42,.88)" : "rgba(234,241,250,.86)",
      border: dark ? "1px solid rgba(96,165,250,.18)" : "1px solid rgba(150,174,205,.22)",
    },
    navBtn: {
      border: "none",
      borderRadius: 14,
      padding: "11px 16px",
      background: "transparent",
      color: dark ? "#aabbd1" : "#4e6788",
      fontWeight: 900,
      cursor: "pointer",
      margin: 0,
    },
    navBtnActive: {
      background: "linear-gradient(135deg,#3182f6,#38bdf8)",
      color: "#fff",
      boxShadow: "0 10px 24px rgba(49,130,246,.24)",
    },
    intelPanel: {
      borderRadius: 26,
      padding: 22,
      background: dark
        ? "linear-gradient(180deg, rgba(18,40,68,.98), rgba(12,30,54,.98))"
        : "linear-gradient(180deg,#fff,#f7fbff)",
      border: dark ? "1px solid rgba(123,186,255,.20)" : "1px solid rgba(150,174,205,.20)",
      boxShadow: dark ? "0 24px 48px rgba(0,0,0,.26)" : "0 16px 36px rgba(24,55,96,.08)",
    },
    intelEyebrow: {
      color: "#27c7c9",
      fontSize: 12,
      fontWeight: 900,
      letterSpacing: ".14em",
      textTransform: "uppercase",
    },
    intelTitle: {
      margin: "7px 0 6px",
      fontSize: "clamp(1.55rem,3vw,2rem)",
      fontWeight: 900,
      color: dark ? "#f4f7fb" : "#112c53",
    },
    intelCopy: {
      color: dark ? "#9eb6d0" : "#607897",
      lineHeight: 1.55,
    },
    metricGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))",
      gap: 14,
      marginTop: 18,
    },
    metricCard: {
      padding: 18,
      borderRadius: 18,
      background: dark ? "rgba(13,34,61,.82)" : "#fff",
      border: dark ? "1px solid rgba(96,165,250,.16)" : "1px solid rgba(150,174,205,.18)",
    },
    metricLabel: {
      fontSize: 11,
      fontWeight: 900,
      letterSpacing: ".11em",
      textTransform: "uppercase",
      color: dark ? "#89aedf" : "#6d83a2",
    },
    metricValue: {
      marginTop: 8,
      fontSize: 26,
      fontWeight: 900,
      color: dark ? "#f4f7fb" : "#112c53",
    },
    gatewayGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))",
      gap: 14,
      marginTop: 18,
    },
    gatewayCard: {
      padding: 18,
      borderRadius: 18,
      background: dark ? "rgba(10,29,52,.72)" : "rgba(249,252,255,.98)",
      border: dark ? "1px solid rgba(96,165,250,.14)" : "1px solid rgba(150,174,205,.18)",
    },
    gatewayTitle: { fontWeight: 900, fontSize: 17, color: dark ? "#f4f7fb" : "#112c53" },
    gatewayText: { marginTop: 6, color: dark ? "#9eb6d0" : "#607897", fontSize: 13, lineHeight: 1.5 },
    inlineAction: {
      marginTop: 14,
      border: dark ? "1px solid rgba(96,165,250,.24)" : "1px solid rgba(47,111,242,.20)",
      background: dark ? "rgba(59,130,246,.10)" : "#fff",
      color: dark ? "#9dccff" : "#2f6ff2",
      borderRadius: 12,
      padding: "10px 12px",
      fontWeight: 900,
      cursor: "pointer",
      marginRight: 0,
    },
    phasePill: {
      display: "inline-flex",
      marginTop: 12,
      padding: "6px 10px",
      borderRadius: 999,
      background: dark ? "rgba(245,158,11,.10)" : "rgba(245,158,11,.10)",
      border: "1px solid rgba(245,158,11,.22)",
      color: dark ? "#fbbf24" : "#b56b00",
      fontSize: 11,
      fontWeight: 900,
    },
    backBtn: {
      width: "100%",
      background: dark
        ? "linear-gradient(135deg, rgba(8,30,61,0.98) 0%, rgba(13,52,100,0.98) 100%)"
        : "linear-gradient(135deg, rgba(230,239,251,0.98) 0%, rgba(218,232,250,0.98) 100%)",
      color: dark ? "#f4f7fb" : "#12335f",
      border: dark ? "1px solid rgba(67, 113, 181, 0.2)" : "1px solid rgba(150, 174, 205, 0.22)",
      borderRadius: 20,
      padding: "18px 18px",
      fontSize: 20,
      fontWeight: 900,
      cursor: "pointer",
      boxShadow: dark ? "0 18px 38px rgba(0,0,0,0.22)" : "0 16px 34px rgba(24, 55, 96, 0.08)",
      textAlign: "left",
    },
  };
}

export default function ReportsPage() {  
  const { themeMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const boundStoreName = useBoundStoreName();
  const storeId = localStorage.getItem("storeId") || "";
  const storeName =
    boundStoreName ||
    localStorage.getItem("storeName") ||
    localStorage.getItem("storeLabel") ||
    "Store";
  const { orgId } = useMemo(() => getOrg() || { orgId: "" }, []);
  const styles = useMemo(() => getStyles(themeMode), [themeMode]);

  const [dateFilter, setDateFilter] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return format(d, "yyyy-MM-dd");
  });
  const [sections, setSections] = useState([]);
  const [expandedSection, setExpandedSection] = useState(null);
  const [submissions, setSubmissions] = useState({});
  const [liveDrafts, setLiveDrafts] = useState({});
  const [expandedSubmission, setExpandedSubmission] = useState({});
  const [clockinData, setClockinData] = useState([]);
  const [filteredClockinData, setFilteredClockinData] = useState([]);
  const [toast, setToast] = useState({ message: "", type: "info" });
  const [now, setNow] = useState(new Date());
  const [neoDutyTargetHandled, setNeoDutyTargetHandled] = useState(false);
  const [highlightedDutyKey, setHighlightedDutyKey] = useState("");
  const neoDutySubmissionOpenedRef = useRef(false);
  const [reportView, setReportView] = useState("history");
  const [publishedStandards, setPublishedStandards] = useState([]);
  const [carryOverRows, setCarryOverRows] = useState([]);
  const [carryOverLoading, setCarryOverLoading] = useState(false);
  const [pendingScheduleDecisions, setPendingScheduleDecisions] = useState([]);
  const [selectedPendingDecision, setSelectedPendingDecision] = useState(null);
  const [decisionSaving, setDecisionSaving] = useState(false);
  const [outstandingPeriod, setOutstandingPeriod] = useState("");
  const [historyDutyEvents, setHistoryDutyEvents] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [decisionHistoryEvents, setDecisionHistoryEvents] = useState([]);
  const [decisionHistoryLoading, setDecisionHistoryLoading] = useState(false);

  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280
  );

  const isPhone = viewportWidth < 640;
  const isTablet = viewportWidth >= 640 && viewportWidth < 1024;
  const reportContentRef = useRef(null);

  useEffect(() => {
    if (location.state?.fromNeoDuty) return;
    const timer = window.setTimeout(() => {
      reportContentRef.current?.scrollIntoView({
        behavior: "auto",
        block: "start",
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [reportView, location.state]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!toast.message) return;
    const t = setTimeout(() => setToast({ message: "", type: "info" }), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const loadSections = useCallback(async () => {
    if (!orgId || !storeId) return [];
    const [builder, content, publishedStandards] = await Promise.all([
      fetchBuilderSections(orgId, storeId),
      fetchContentSections(orgId),
      fetchPublishedStandards(orgId, storeId),
    ]);

    setPublishedStandards(publishedStandards);
    const merged = buildReportSections(builder, content);
    return filterReportSectionsByPublishedStandards(merged, publishedStandards);
  }, [orgId, storeId]);

  useEffect(() => {
    (async () => {
      const merged = await loadSections();
      setSections(merged);
      setExpandedSection(null);
    })();
  }, [loadSections]);


  const refreshCarryOver = useCallback(async () => {
    if (!orgId || !storeId) {
      setCarryOverRows([]);
      return;
    }

    setCarryOverLoading(true);
    const [rows, decisions] = await Promise.all([
      fetchCarryOverDuties(orgId, storeId, publishedStandards),
      fetchPendingScheduleDecisions(orgId, storeId),
    ]);
    setCarryOverRows(rows);
    setPendingScheduleDecisions(decisions);
    setCarryOverLoading(false);
  }, [orgId, storeId, publishedStandards]);

  useEffect(() => {
    if (reportView !== "outstanding") return;
    refreshCarryOver();
  }, [reportView, refreshCarryOver]);


  const refreshOperationalHistory = useCallback(async () => {
    if (!orgId || !storeId || !dateFilter) {
      setHistoryDutyEvents([]);
      return;
    }

    setHistoryLoading(true);
    const rows = await fetchHistoricalDutyEvents(orgId, storeId, dateFilter, outstandingPeriod);
    setHistoryDutyEvents(rows);
    setHistoryLoading(false);
  }, [orgId, storeId, dateFilter, outstandingPeriod]);

  useEffect(() => {
    if (reportView !== "history") return;
    refreshOperationalHistory();
  }, [reportView, refreshOperationalHistory]);


  const refreshDecisionHistory = useCallback(async () => {
    if (!orgId || !storeId || !dateFilter) {
      setDecisionHistoryEvents([]);
      return;
    }

    setDecisionHistoryLoading(true);
    const rows = await fetchDecisionResponseHistory(orgId, storeId, dateFilter, outstandingPeriod);
    setDecisionHistoryEvents(rows);
    setDecisionHistoryLoading(false);
  }, [orgId, storeId, dateFilter, outstandingPeriod]);

  useEffect(() => {
    if (reportView !== "decisions") return;
    refreshDecisionHistory();
  }, [reportView, refreshDecisionHistory]);




  const fetchClockIns = useCallback(async () => {
    try {
      let rows = [];
      try {
        const snapNew = await getDocs(
          collection(db, "orgs", orgId, "stores", storeId, "shifts")
        );
        rows = snapNew.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((s) => !isArchivedRecord(s));
      } catch {
        rows = [];
      }

      if (!rows.length) {
        const snapOld = await getDocs(collection(db, "cashup_shifts"));
        const all = snapOld.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows = all.filter((s) => (s.storeId === storeId || s.store === storeId) && !isArchivedRecord(s));
      }

      const byDate = rows
        .filter((s) => {
          const ci = tsToDate(s.clockIn);
          return ci && reportDateMatchesRange(ci, outstandingPeriod, dateFilter);
        })
        .sort((a, b) => {
          const aMs = tsToDate(a.clockIn)?.getTime?.() || 0;
          const bMs = tsToDate(b.clockIn)?.getTime?.() || 0;
          return aMs - bMs;
        });

      setClockinData(rows);
      setFilteredClockinData(byDate);
    } catch (e) {
      console.error(e);
      setToast({ message: "Failed to load clock-ins.", type: "error" });
    }
  }, [orgId, storeId, dateFilter, outstandingPeriod]);

  // C6: filters are the refresh action for historical attendance. This is an
  // on-demand read caused by user interaction, not a background listener.
  useEffect(() => {
    if (reportView !== "history") return;
    fetchClockIns();
  }, [reportView, fetchClockIns]);

  const fetchSubmissionsFor = useCallback(
    async (sec, explicitDate = "") => {
      const effectiveDate = explicitDate || dateFilter;
      try {
        let all = [];
        try {
          const snapNew = await getDocs(
            collection(db, "orgs", orgId, "stores", storeId, "submissions")
          );
          all = snapNew.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((s) => !isArchivedRecord(s));
        } catch {
          all = [];
        }

        if (!all.length) {
          const q1 = query(
            collection(db, "submissions"),
            where("orgId", "==", orgId),
            where("storeId", "==", storeId)
          );
          const snap = await getDocs(q1);
          all = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((s) => !isArchivedRecord(s));
        }

        let todays = all
          .filter((s) => {
            const byId = [sec.id, sec._match?.preferId, sec._match?.builderId].filter(Boolean).includes(s.sectionId);
            const byName = norm(s.sectionName) === norm(sec.name);
            const byCustom = sec.id === "custom_report_fixed" && (s.sectionId === "custom_report_fixed" || norm(s.sectionName) === "custom report");
            if (!(byId || byName || byCustom)) return false;
            const when = tsToDate(s.submittedAt) || tsToDate(s.timestamp);
            return when && (
              explicitDate
                ? format(when, "yyyy-MM-dd") === effectiveDate
                : reportDateMatchesRange(when, outstandingPeriod, dateFilter)
            );
          })
          .sort((a, b) => {
            const ta = tsToDate(a.submittedAt) || tsToDate(a.timestamp);
            const tb = tsToDate(b.submittedAt) || tsToDate(b.timestamp);
            return (tb?.getTime?.() || 0) - (ta?.getTime?.() || 0);
          });

        const needsLegacyEvidence = todays.some((submission) =>
          !submission.morpheusCompletions && fieldsForSubmission(sec, submission).some(isMorpheusField)
        );
        if (needsLegacyEvidence) {
          try {
            // On-demand, site-scoped compatibility read for reports that were
            // submitted before Morpheus completion snapshots were included.
            const evidence = await getDocs(query(
              collection(db, "orgs", orgId, "neoDutyQueue"),
              where("storeId", "==", storeId)
            ));
            const rows = evidence.docs.map((item) => ({ ...item.data(), id: item.id }));
            todays = todays.map((submission) => restoreLegacyMorpheusSubmission(sec, submission, rows, storeId));
          } catch (error) {
            console.error("Morpheus report evidence failed to load", error);
            setToast({ message: "Reports loaded, but Morpheus completion details could not be loaded. Reopen the section to retry.", type: "error" });
          }
        }

        setSubmissions((prev) => ({ ...prev, [sec.id]: todays }));
      } catch (e) {
        console.error(e);
        setToast({ message: "Failed to load submissions.", type: "error" });
      }
    },
    [orgId, storeId, dateFilter, outstandingPeriod]
  );

  const fetchDraftsFor = useCallback(
    async (sec, explicitDate = "") => {
      const effectiveDate = explicitDate || dateFilter;
      try {
        const snap = await getDocs(
          collection(db, "orgs", orgId, "stores", storeId, "dashDrafts")
        );
        const drafts = snap.docs
          .map((d) => ({ id: `draft_${d.id}`, ...d.data(), __liveDraft: true }))
          .filter((d) => !isArchivedRecord(d))
          .filter((d) => {
            const byId = d.sectionId === sec.id || (sec._match?.preferId && d.sectionId === sec._match.preferId);
            const byName = norm(d.sectionName) === norm(sec.name);
            if (!(byId || byName) || !hasReportProgress(d.values)) return false;
            const when = tsToDate(d.updatedAt) || tsToDate(d.savedAt);
            return when && (
              explicitDate
                ? format(when, "yyyy-MM-dd") === effectiveDate
                : reportDateMatchesRange(when, outstandingPeriod, dateFilter)
            );
          })
          .sort((a, b) => {
            const ta = tsToDate(a.updatedAt) || tsToDate(a.savedAt);
            const tb = tsToDate(b.updatedAt) || tsToDate(b.savedAt);
            return (tb?.getTime?.() || 0) - (ta?.getTime?.() || 0);
          });
        setLiveDrafts((prev) => ({ ...prev, [sec.id]: drafts }));
      } catch (e) {
        console.error(e);
        setToast({ message: "Workspace progress failed to load.", type: "error" });
      }
    },
    [orgId, storeId, dateFilter, outstandingPeriod]
  );

  useEffect(() => {
    const target = location.state;
    if (!target?.fromNeoDuty || neoDutyTargetHandled || !sections.length) return;

    // Duty deep-links always land inside Standards & Duties on the exact
    // operating date. This is also used by Operational Trail history cards.
    setReportView("standards");
    setOutstandingPeriod("");

    const targetId = String(target.sectionId || target.standardId || "");
    const targetName = norm(target.sectionName || "");
    const sec =
      sections.find((s) => targetId && (s.id === targetId || s?._match?.preferId === targetId)) ||
      sections.find((s) => targetName && norm(s.name) === targetName);

    if (!sec) return;

    const dueDate = tsToDate(target.dueAt);
    const targetDate = dueDate ? format(dueDate, "yyyy-MM-dd") : dateFilter;

    // Important: React state updates asynchronously. Pass targetDate directly
    // to the fetch so Open Duty never loads the previously selected report date.
    if (targetDate !== dateFilter) setDateFilter(targetDate);

    setExpandedSection(sec.id);
    setExpandedSubmission({});
    setNeoDutyTargetHandled(true);

    window.setTimeout(async () => {
      if (sec.id !== CLOCKIN_VIRTUAL_ID) {
        await Promise.all([
          fetchSubmissionsFor(sec, targetDate),
          fetchDraftsFor(sec, targetDate),
        ]);
      }
      document.getElementById(`neo-report-section-${sec.id}`)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 80);
  }, [location.state, neoDutyTargetHandled, sections, fetchSubmissionsFor, fetchDraftsFor, dateFilter]);

  useEffect(() => {
    const target = location.state;
    if (!target?.fromNeoDuty || !neoDutyTargetHandled || !expandedSection || neoDutySubmissionOpenedRef.current) return;
    const rows = [
      ...(liveDrafts[expandedSection] || []),
      ...(submissions[expandedSection] || []),
    ];
    if (!rows.length) return;

    const targetDutyId = String(target.dutyId || "");
    const targetNeoDutyId = String(target.neoDutyId || "");
    const targetDutyLabel = norm(target.dutyLabel || "");
    const sec = sections.find((s) => s.id === expandedSection);
    const targetVisibleFieldIndex = Array.isArray(sec?.fields)
      ? sec.fields.findIndex((field) => targetDutyLabel && norm(field?.label || "") === targetDutyLabel)
      : -1;
    const targetFieldIndex =
      targetVisibleFieldIndex >= 0
        ? (Number.isInteger(sec?.fields?.[targetVisibleFieldIndex]?.__sourceIndex)
            ? sec.fields[targetVisibleFieldIndex].__sourceIndex
            : targetVisibleFieldIndex)
        : -1;
    const targetFieldType =
      targetVisibleFieldIndex >= 0
        ? sec?.fields?.[targetVisibleFieldIndex]?.type || ""
        : "";
    const targetField = targetVisibleFieldIndex >= 0
      ? { ...sec.fields[targetVisibleFieldIndex], idx: targetFieldIndex, type: targetFieldType }
      : null;

    const sortedRows = rows
      .slice()
      .sort((a, b) => {
        const aTime = tsToDate(a.submittedAt || a.timestamp || a.updatedAt || a.savedAt)?.getTime?.() || 0;
        const bTime = tsToDate(b.submittedAt || b.timestamp || b.updatedAt || b.savedAt)?.getTime?.() || 0;
        return bTime - aTime;
      });

    const exactByIdentity =
      sortedRows.find((row) => targetDutyId && (
        String(row.dutyId || "") === targetDutyId ||
        String(row.neoDutyId || "") === targetDutyId
      )) ||
      sortedRows.find((row) => targetNeoDutyId && String(row.neoDutyId || "") === targetNeoDutyId);

    const exactByField =
      targetFieldIndex >= 0
        ? sortedRows.find((row) => reportItemComplete(targetField, row.values, row))
        : null;

    const exactByLabel = sortedRows.find((row) => targetDutyLabel && (
      norm(row.dutyLabel || row.label || row.title || "") === targetDutyLabel
    ));

    const targetRow = exactByIdentity || exactByField || exactByLabel || sortedRows[0];

    if (targetRow?.id) {
      neoDutySubmissionOpenedRef.current = true;
      setExpandedSubmission((prev) => ({ ...prev, [expandedSection]: targetRow.id }));

      const fieldCompleted =
        targetFieldIndex >= 0 &&
        reportItemComplete(targetField, targetRow?.values, targetRow);

      // A submitted report is completed (live drafts are explicitly incomplete).
      // If the exact duty field is present and completed, repair an old/stale
      // neoDutyQueue item so it no longer remains in Operational Exceptions.
      if (targetNeoDutyId && !targetNeoDutyId.startsWith("nm_") && !isMorpheusField(targetField) && target.completionSource !== "morpheus" && fieldCompleted && !targetRow.__liveDraft) {
        updateDoc(doc(db, "orgs", orgId, "neoDutyQueue", targetNeoDutyId), {
          completed: true,
          status: "completed",
          completedAt: targetRow.submittedAt || targetRow.timestamp || serverTimestamp(),
          completedBy:
            targetRow.submittedBy ||
            targetRow.submittedByName ||
            targetRow.savedBy ||
            targetRow.userName ||
            "",
          completionSource: "manager_report_reconciliation",
          reconciledAt: serverTimestamp(),
        }).catch((error) => {
          console.warn("Neo duty reconciliation skipped", error);
        });
      }

      window.setTimeout(() => {
        const fieldKey = targetDutyLabel ? domKey(targetDutyLabel) : "";
        const fieldEl = fieldKey
          ? document.getElementById(`neo-report-field-${fieldKey}`)
          : null;
        const rowEl = document.getElementById(`neo-report-submission-${targetRow.id}`);

        if (fieldKey && fieldEl) {
          setHighlightedDutyKey(fieldKey);
          window.setTimeout(() => {
            setHighlightedDutyKey((current) => current === fieldKey ? "" : current);
          }, 3200);
        }

        (fieldEl || rowEl)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 180);
    }
  }, [location.state, neoDutyTargetHandled, expandedSection, submissions, liveDrafts, sections, orgId]);

  const toggleSection = async (secId) => {
    if (expandedSection === secId) {
      setExpandedSection(null);
      return;
    }
    setExpandedSection(secId);
    setExpandedSubmission({});

    const sec = sections.find((s) => s.id === secId);
    if (!sec) return;

    // The C6 on-demand effect below performs the single read for the newly
    // opened section (and again only when the user changes a report filter).
  };

  const toggleSubmission = (secId, subId) => {
    setExpandedSubmission((prev) => ({
      ...prev,
      [secId]: prev[secId] === subId ? null : subId,
    }));
  };

  const refetchCurrent = useCallback(() => {
    if (expandedSection === CLOCKIN_VIRTUAL_ID) {
      fetchClockIns();
    } else if (expandedSection) {
      const sec = sections.find((s) => s.id === expandedSection);
      if (sec) Promise.all([fetchSubmissionsFor(sec), fetchDraftsFor(sec)]);
    }
  }, [expandedSection, sections, fetchClockIns, fetchSubmissionsFor, fetchDraftsFor]);

  // C6.4: Standards & Duties stays asleep until a section is open. Once open,
  // changing the report period is itself the user's refresh action for that
  // section. Keep the section open, fetch only that section for the new range,
  // and never wake the other report sections.
  useEffect(() => {
    if (reportView !== "standards" || !expandedSection) return;

    if (expandedSection === CLOCKIN_VIRTUAL_ID) {
      fetchClockIns();
      return;
    }

    const sec = sections.find((s) => s.id === expandedSection);
    if (sec) {
      Promise.all([fetchSubmissionsFor(sec), fetchDraftsFor(sec)]);
    }
  }, [
    reportView,
    expandedSection,
    sections,
    fetchClockIns,
    fetchSubmissionsFor,
    fetchDraftsFor,
  ]);

  // C6 UX: changing the reporting period keeps the currently opened report
  // section open, but collapses any expanded submission rows inside it. The
  // on-demand effect above then refetches that open section for the new period.
  // This preserves filter responsiveness without waking unopened sections.
  const standardsFilterKey = `${outstandingPeriod || "date"}:${dateFilter}`;
  const previousStandardsFilterKeyRef = useRef(standardsFilterKey);
  useEffect(() => {
    const previousKey = previousStandardsFilterKeyRef.current;
    previousStandardsFilterKeyRef.current = standardsFilterKey;
    if (previousKey === standardsFilterKey || reportView !== "standards") return;
    setExpandedSubmission({});
  }, [standardsFilterKey, reportView]);

  // C6: Reports are historical/non-live intelligence. Clock-ins, submissions and
  // workspace drafts are fetched only when the user opens/refreshes that section.
  // No Firestore listeners remain mounted while Reports sits open.

  function renderSubmission(sec, sub) {
    if (sub?.customReport) {
      const blocks = sub.customReport.items || [];
      const title = sub.customReport.title || "Custom Report";
      return (
        <div>
          <div style={{ ...styles.bold, color: "#1e90ff", marginBottom: 12 }}>{title}</div>
          <table style={styles.subTable}>
            <thead>
              <tr>
                <th style={styles.thLeft}>Field</th>
                <th style={styles.thRight}>Value</th>
              </tr>
            </thead>
            <tbody>
              {blocks.map((item, idx) => {
                if (item.type === "subheading") {
                  return (
                    <tr key={item.id || idx}>
                      <td colSpan={2} style={styles.subheadCell}>{item.value || "Subheading"}</td>
                    </tr>
                  );
                }

                let valueNode = null;
                if (item.type === "check") {
                  valueNode = item.value === true ? "Yes" : item.value === false ? "No" : "—";
                } else if (item.type === "photo") {
                  const items = Array.isArray(item.value) ? item.value : item.value ? [item.value] : [];
                  valueNode = (
                    <div style={styles.fileList}>
                      {items.map((it, pIdx) => (
                        <a key={pIdx} href={it.url || it} target="_blank" rel="noopener noreferrer" style={styles.fileLink}>
                          <img src={it.url || it} alt={item.label || "photo"} style={styles.thumb} />
                        </a>
                      ))}
                    </div>
                  );
                } else {
                  valueNode = String(item.value || "");
                }

                return (
                  <tr key={item.id || idx}>
                    <td style={styles.tdLeft}>{item.label || "Field"}</td>
                    <td style={styles.tdRight}>{valueNode}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    const fields = fieldsForSubmission(sec, sub);
    if (!fields.length) return null;

    const isUrl = (x) => typeof x === "string" && /^https?:\/\//i.test(x);

    const renderFileCell = (val, label) => {
      const items = Array.isArray(val) ? val : [val];
      return (
        <div style={styles.fileList}>
          {items.map((it, idx) => {
            let url = "";
            let name = "";
            if (it && typeof it === "object") {
              url = it.url || "";
              name = it.name || label || "file";
            } else if (isUrl(it)) {
              url = it;
              name = label || "file";
            }
            if (!url) return null;

            const contentType =
              (it && typeof it === "object" && (it.contentType || it.type)) || "";
            const looksImageByExt = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url);
            const isImage = looksImageByExt || /^image\//i.test(contentType);

            return (
              <a
                key={idx}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={name}
                style={styles.fileLink}
              >
                {isImage ? (
                  <img src={url} alt={name} style={styles.thumb} />
                ) : (
                  <span style={styles.fileChip}>View file</span>
                )}
              </a>
            );
          })}
        </div>
      );
    };

    const groups = [];
    let g = null;
    (fields || []).forEach((f, i) => {
      const subh = (f.subheading || "").trim();
      if (!g || g.sub !== subh) {
        g = { sub: subh, firstIdx: i, items: [] };
        groups.push(g);
      }
      g.items.push({
        ...f,
        label: f.label || "",
        type: f.type || "",
        idx: Number.isInteger(f?.__sourceIndex) ? f.__sourceIndex : i,
      });
    });

    return (
      <table style={styles.subTable}>
        <thead>
          <tr>
            <th style={styles.thLeft}>Field</th>
            <th style={styles.thRight}>Value</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((grp, gi) => {
            const saveMeta = subheadingSaveMeta(sub.subheadingSaves, grp.sub, grp.firstIdx);
            const grpStats = completionStatsForItems(grp.items, sub.values || {}, sub);
            return (
              <React.Fragment key={gi}>
                {grp.sub && (
                  <>
                    <tr>
                      <td style={styles.subheadCell} colSpan={2}>
                        <div style={styles.subheadInner}>
                          <span>{grp.sub}</span>
                          <span style={styles.progressPill}>{formatProgress(grpStats)} done</span>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2} style={styles.subMetaCell}>
                        {saveMeta?.savedBy ? (
                          <>
                            Saved by <strong>{saveMeta.savedBy}</strong>
                            {saveMeta?.savedAt
                              ? ` at ${format(tsToDate(saveMeta.savedAt), "dd MMM yyyy, HH:mm")}`
                              : ""}
                          </>
                        ) : grp.items.every(isMorpheusField) ? (
                          <em>Count completion is recorded in Morpheus.</em>
                        ) : (
                          <em>No saved-by info</em>
                        )}
                      </td>
                    </tr>
                  </>
                )}
                {grp.items.map((it) => {
                  const v = sub.values?.[it.idx];
                  if (!isMorpheusField(it) && (v == null || v === "")) return null;
                  return (
                    <tr
                      key={it.idx}
                      id={`neo-report-field-${domKey(it.label)}`}
                    >
                      <td
                        style={{
                          ...styles.tdLeft,
                          ...(highlightedDutyKey === domKey(it.label)
                            ? {
                                background: themeMode === "dark"
                                  ? "rgba(30,144,255,.12)"
                                  : "rgba(30,144,255,.08)",
                                boxShadow: themeMode === "dark"
                                  ? "inset 2px 0 0 rgba(96,165,250,.72), 0 0 18px rgba(59,130,246,.16)"
                                  : "inset 2px 0 0 rgba(37,99,235,.52), 0 0 16px rgba(37,99,235,.10)",
                                transition: "background .35s ease, box-shadow .35s ease",
                              }
                            : {}),
                        }}
                      >
                        {it.label}
                      </td>
                      <td
                        style={{
                          ...styles.tdRight,
                          ...(highlightedDutyKey === domKey(it.label)
                            ? {
                                background: themeMode === "dark"
                                  ? "rgba(30,144,255,.12)"
                                  : "rgba(30,144,255,.08)",
                                boxShadow: themeMode === "dark"
                                  ? "inset -2px 0 0 rgba(96,165,250,.72), 0 0 18px rgba(59,130,246,.16)"
                                  : "inset -2px 0 0 rgba(37,99,235,.52), 0 0 16px rgba(37,99,235,.10)",
                                transition: "background .35s ease, box-shadow .35s ease",
                              }
                            : {}),
                        }}
                      >
                        {isMorpheusField(it)
                          ? <MorpheusReportValue result={morpheusResultForField(it, sub)} />
                          : it.type === "photo"
                          ? renderFileCell(v, it.label)
                          : it.type === "checkbox"
                          ? v === true
                            ? "Yes"
                            : v === false
                            ? "No"
                            : "—"
                          : String(v)}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  }

  const openCarryOverRecord = useCallback((row) => {
    const dutyDate = reportDateKey(reportDutyOperationalDate(row));
    if (dutyDate) setDateFilter(dutyDate);

    const targetId = String(row.sectionId || row.standardId || "");
    const targetName = norm(row.sectionName || row.standardName || row.categoryName || "");
    const sec =
      sections.find((s) => targetId && (s.id === targetId || s?._match?.preferId === targetId)) ||
      sections.find((s) => targetName && norm(s.name) === targetName);

    setReportView("standards");

    if (sec) {
      setExpandedSection(sec.id);
      setExpandedSubmission({});
      window.setTimeout(() => {
        fetchSubmissionsFor(sec, dutyDate || dateFilter);
        document.getElementById(`neo-report-section-${sec.id}`)?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 80);
    }
  }, [sections, fetchSubmissionsFor, dateFilter]);

  const openHistoryDutyRecord = useCallback((row) => {
    const dutyDate = reportDateKey(reportDutyOperationalDate(row)) || reportDateKey(row.historyEventTime) || dateFilter;

    setNeoDutyTargetHandled(false);
    setHighlightedDutyKey("");
    setReportView("standards");
    setOutstandingPeriod("");

    navigate("/manager-reports", {
      replace: true,
      state: {
        fromNeoDuty: true,
        fromOperationalTrail: true,
        sectionId: row.sectionId || row.standardId || "",
        standardId: row.standardId || row.sectionId || "",
        sectionName: row.sectionName || row.standardName || row.categoryName || row.category || "",
        dutyId: row.dutyId || row.standardDutyId || row.sourceDutyId || "",
        neoDutyId: row.id || "",
        dutyLabel: reportDutyLabel(row),
        dueAt: dutyDate ? `${dutyDate}T12:00:00` : null,
        reportDate: dutyDate,
      },
    });
  }, [navigate, dateFilter]);



  const historyMetrics = useMemo(() => {
    const completed = historyDutyEvents.filter((row) => row.historyStatus === "completed").length;
    const missed = historyDutyEvents.filter((row) => row.historyStatus === "missed").length;
    const escalated = historyDutyEvents.filter((row) => row.historyStatus === "escalated").length;
    const open = historyDutyEvents.filter((row) => row.historyStatus === "open").length;

    return {
      totalDuties: historyDutyEvents.length,
      completed,
      missed,
      escalated,
      open,
    };
  }, [historyDutyEvents]);

  const historyShiftRows = useMemo(
    () =>
      (filteredClockinData || []).slice().sort((a, b) => {
        const aMs = tsToDate(a.clockIn)?.getTime?.() || 0;
        const bMs = tsToDate(b.clockIn)?.getTime?.() || 0;
        return bMs - aMs;
      }),
    [filteredClockinData]
  );

  const reportsManagerActor = useMemo(() => {
    try {
      const session = JSON.parse(
        sessionStorage.getItem("mgrSession") ||
        localStorage.getItem("mgrSession") ||
        "{}"
      ) || {};
      return {
        name: String(session.managerName || localStorage.getItem("managerName") || "Manager"),
        sessionToken: String(session.serverSessionToken || ""),
      };
    } catch {
      return { name: "Manager", sessionToken: "" };
    }
  }, []);

  const decidePendingScheduleException = useCallback(async (row, approved) => {
    if (!row || !orgId || !storeId || !row.shiftId) return;
    const sessionToken = String(reportsManagerActor.sessionToken || "");
    if (!sessionToken) {
      setToast({ message: "Your secure manager session is missing. Please log in again.", type: "error" });
      return;
    }

    setDecisionSaving(true);
    try {
      const functions = getFunctions(app, "us-central1");
      const decide = httpsCallable(functions, "decideNeoScheduleException");
      const scheduleEntryId =
        row.scheduleEntryId ||
        `${storeId}__${String(row.staffId || "").trim()}__${String(row.date || "")}`;

      const result = await decide({
        sessionToken,
        orgId,
        storeId,
        scheduleEntryId,
        shiftId: String(row.rawShiftId || row.shiftId || "").replace(/^(nested|legacy):/, ""),
        staffId: String(row.staffId || ""),
        staffName: String(row.staffName || "Staff member"),
        date: String(row.date || ""),
        decision: approved ? "approve" : "reject",
        varianceType: String(row.exceptionType || "overtime"),
        intelligenceLabel: String(row.intelligenceLabel || ""),
      });

      if (result?.data?.ok !== true) throw new Error("Decision not confirmed");

      setToast({
        message: approved
          ? `${row.staffName}'s exception was approved.`
          : `${row.staffName}'s exception was rejected.`,
        type: "success",
      });
      setSelectedPendingDecision(null);
      await refreshCarryOver();
    } catch (error) {
      console.error("[Neo Reports MR2.4] decision failed", error);
      setToast({ message: "Could not save the schedule decision.", type: "error" });
    } finally {
      setDecisionSaving(false);
    }
  }, [orgId, storeId, reportsManagerActor.sessionToken, refreshCarryOver]);

  const outstandingPeriodRange = useMemo(() => {
    const today = new Date();
    const mondayOptions = { weekStartsOn: 1 };

    if (outstandingPeriod === "thisWeek") {
      return {
        start: startOfWeek(today, mondayOptions),
        end: endOfWeek(today, mondayOptions),
      };
    }

    if (outstandingPeriod === "lastWeek") {
      const ref = subWeeks(today, 1);
      return {
        start: startOfWeek(ref, mondayOptions),
        end: endOfWeek(ref, mondayOptions),
      };
    }

    if (outstandingPeriod === "thisMonth") {
      return {
        start: startOfMonth(today),
        end: endOfMonth(today),
      };
    }

    if (outstandingPeriod === "lastMonth") {
      const ref = subMonths(today, 1);
      return {
        start: startOfMonth(ref),
        end: endOfMonth(ref),
      };
    }

    // No period filter selected = the Reports default behaviour:
    // show only the date currently selected in the date picker.
    const selected = new Date(`${dateFilter}T12:00:00`);
    const safeSelected = Number.isNaN(selected.getTime()) ? today : selected;

    return {
      start: safeSelected,
      end: safeSelected,
    };
  }, [outstandingPeriod, dateFilter]);

  const periodIncludesDateKey = useCallback((dateKey) => {
    if (!dateKey) return false;
    const parsed = new Date(`${dateKey}T12:00:00`);
    if (Number.isNaN(parsed.getTime())) return false;

    const start = new Date(outstandingPeriodRange.start);
    const end = new Date(outstandingPeriodRange.end);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    return parsed.getTime() >= start.getTime() && parsed.getTime() <= end.getTime();
  }, [outstandingPeriodRange]);

  const filteredPendingScheduleDecisions = useMemo(
    () =>
      (pendingScheduleDecisions || [])
        .filter((row) => periodIncludesDateKey(String(row.date || "")))
        .sort((a, b) => {
          const aDate = String(a.date || "");
          const bDate = String(b.date || "");
          if (aDate !== bDate) return bDate.localeCompare(aDate);

          const aName = String(a.staffName || "");
          const bName = String(b.staffName || "");
          return aName.localeCompare(bName);
        }),
    [pendingScheduleDecisions, periodIncludesDateKey]
  );

  const filteredCarryOverRows = useMemo(
    () =>
      (carryOverRows || [])
        .filter((row) => periodIncludesDateKey(reportDateKey(reportDutyOperationalDate(row))))
        .sort((a, b) => {
          const aMs = tsToDate(reportDutyOperationalDate(a))?.getTime?.() || 0;
          const bMs = tsToDate(reportDutyOperationalDate(b))?.getTime?.() || 0;
          return bMs - aMs;
        }),
    [carryOverRows, periodIncludesDateKey]
  );

  const carryOverMetrics = useMemo(() => {
    const yesterday = format(
      new Date(new Date().setDate(new Date().getDate() - 1)),
      "yyyy-MM-dd"
    );
    const dutiesFromYesterday = filteredCarryOverRows.filter(
      (row) => reportDateKey(reportDutyOperationalDate(row)) === yesterday
    ).length;
    const decisionsFromYesterday = filteredPendingScheduleDecisions.filter(
      (row) => String(row.date || "") === yesterday
    ).length;

    return {
      total: filteredCarryOverRows.length + filteredPendingScheduleDecisions.length,
      actionableDuties: filteredCarryOverRows.length,
      needsDecision: filteredPendingScheduleDecisions.length,
      fromYesterday: dutiesFromYesterday + decisionsFromYesterday,
    };
  }, [filteredCarryOverRows, filteredPendingScheduleDecisions]);

  const timeText = format(now, "HH:mm");
  const dateText = now.toLocaleDateString([], {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <div className="page" style={styles.page}>
      <div style={styles.surface}>
        <div style={styles.hero}>
          <div style={styles.heroGlow} />
          <div style={styles.badge}>Neo Manager Reports</div>

          <div style={styles.heroRow}>
            <div style={styles.heroLeft}>
              <h1 style={styles.title}>
                Reports
                <span style={styles.titleAccent}>{storeName}</span>
              </h1>
              <div style={styles.subtitle}>
                Historical intelligence for what happened, what was missed, what was decided, and what still needs action.
              </div>
            </div>

            <div style={styles.heroRight}>
              <div style={styles.liveTime}>{timeText}</div>
              <div style={styles.liveSub}>{dateText}</div>
              <div style={styles.onlinePill}>Manager view</div>
            </div>
          </div>
        </div>

        <div
          style={{
            ...styles.intelligenceNav,
            width: isPhone || isTablet ? "100%" : styles.intelligenceNav.width,
            ...(isPhone
              ? {
                  flexWrap: "wrap",
                  overflowX: "visible",
                  boxSizing: "border-box",
                  gap: 8,
                }
              : {}),
          }}
        >
          {[
            ["history", "History"],
            ["outstanding", "Outstanding"],
            ["standards", "Standards & Duties"],
            ["decisions", "Decisions"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setReportView(key)}
              style={{
                ...styles.navBtn,
                ...(isPhone
                  ? { flex: "1 1 calc(50% - 4px)", textAlign: "center", minWidth: 0, whiteSpace: "normal" }
                  : isTablet
                  ? { flex: "1 1 140px", textAlign: "center", minWidth: 0 }
                  : {}),
                ...(reportView === key ? styles.navBtnActive : {}),
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <div
          style={{
            ...styles.filterCard,
            alignItems: isPhone ? "stretch" : "center",
            justifyContent: "space-between",
            gap: 18,
            flexDirection: isPhone ? "column" : "row",
            flexWrap: isTablet ? "wrap" : "nowrap",
          }}
        >
          <div
            style={{
              flex: isPhone ? "0 0 auto" : "1 1 360px",
              minWidth: 0,
              maxWidth: isPhone ? "100%" : 520,
            }}
          >
            <div style={styles.filterLabel}>Report Filters</div>
            <div
              style={{
                ...styles.subtitle,
                maxWidth: 500,
                lineHeight: 1.45,
              }}
            >
              Choose a date for the default view, or toggle a period filter when you want a wider range.
            </div>
          </div>

          <div
            style={{
              ...styles.filterRow,
              width: isPhone || isTablet ? "100%" : "auto",
              marginLeft: isPhone || isTablet ? 0 : "auto",
              justifyContent: isPhone ? "stretch" : "flex-end",
              flexWrap: "wrap",
            }}
          >
            {[
              ["thisWeek", "This week"],
              ["lastWeek", "Last week"],
              ["thisMonth", "This month"],
              ["lastMonth", "Last month"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                style={{
                  ...styles.tsBtn,
                  ...(outstandingPeriod === key
                    ? {
                        border: "1px solid rgba(47,111,242,.45)",
                        background: themeMode === "dark"
                          ? "rgba(47,111,242,.16)"
                          : "#eaf2ff",
                        color: themeMode === "dark" ? "#7db5ff" : "#2458d7",
                      }
                    : {}),
                  ...(isPhone ? { flex: "1 1 calc(50% - 6px)" } : {}),
                }}
                onClick={() => {
                  setOutstandingPeriod((current) => (current === key ? "" : key));
                }}
              >
                {label}
              </button>
            ))}

            <input
              type="date"
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setOutstandingPeriod("");
              }}
              style={{
                ...styles.inputDate,
                ...(isPhone ? { flex: "1 1 100%", width: "100%", boxSizing: "border-box" } : {}),
              }}
            />
          </div>
        </div>

        <div ref={reportContentRef} aria-hidden="true" />

        {reportView === "history" ? (
          <div style={styles.intelPanel}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 14,
                alignItems: isPhone ? "stretch" : "flex-start",
                flexDirection: isPhone ? "column" : "row",
              }}
            >
              <div>
                <div style={styles.intelEyebrow}>Operational History</div>
                <div style={styles.intelTitle}>What happened {outstandingPeriod ? reportRangeHeading(outstandingPeriod, dateFilter) : `on ${dateFilter}`}</div>
                <div style={styles.intelCopy}>
                  A historical operating-day view of attendance, duty execution and response activity. Nothing here becomes carry-over unless it is still genuinely actionable.
                </div>
              </div>

              <button
                type="button"
                style={{
                  ...styles.inlineAction,
                  marginTop: 0,
                  ...(isPhone ? { width: "100%" } : {}),
                }}
                onClick={() => {
                  fetchClockIns();
                  refreshOperationalHistory();
                }}
                disabled={historyLoading}
              >
                {historyLoading ? "Refreshing…" : "Refresh history"}
              </button>
            </div>

            <div
              style={{
                ...styles.metricGrid,
                gridTemplateColumns: isPhone
                  ? "1fr"
                  : "repeat(auto-fit,minmax(160px,1fr))",
              }}
            >
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Clocked shifts</div>
                <div style={styles.metricValue}>{historyShiftRows.length}</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Duty events</div>
                <div style={styles.metricValue}>{historyMetrics.totalDuties}</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Completed</div>
                <div style={{ ...styles.metricValue, color: historyMetrics.completed ? "#16a374" : styles.metricValue.color }}>
                  {historyMetrics.completed}
                </div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Missed / expired</div>
                <div style={{ ...styles.metricValue, color: historyMetrics.missed ? "#fb7185" : styles.metricValue.color }}>
                  {historyMetrics.missed}
                </div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Escalated</div>
                <div style={{ ...styles.metricValue, color: historyMetrics.escalated ? "#f59e0b" : styles.metricValue.color }}>
                  {historyMetrics.escalated}
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: 20,
                display: "grid",
                gridTemplateColumns: isPhone
                  ? "1fr"
                  : isTablet
                  ? "1fr"
                  : "minmax(0,1fr) minmax(0,1.15fr)",
                gap: 16,
              }}
            >
              <div style={styles.gatewayCard}>
                <div style={styles.intelEyebrow}>Attendance</div>
                <div style={{ ...styles.gatewayTitle, marginTop: 6 }}>
                  Who worked
                </div>
                <div style={{ ...styles.gatewayText, marginBottom: 14 }}>
                  Clock-in and clock-out activity for the selected operating period.
                </div>

                {historyShiftRows.length === 0 ? (
                  <div style={styles.gatewayText}>No clocked shifts recorded for this period.</div>
                ) : (
                  <div style={{ display: "grid", gap: 10 }}>
                    {historyShiftRows.map((shift) => {
                      const clockIn = tsToDate(shift.clockIn);
                      const clockOut = tsToDate(shift.clockOut);
                      const staffName =
                        shift.name ||
                        shift.staffName ||
                        shift.employeeName ||
                        shift.displayName ||
                        "Staff member";
                      const roleName = shift.roleName || shift.role || "";
                      return (
                        <div
                          key={`history-shift-${shift.id}`}
                          style={{
                            padding: "13px 14px",
                            borderRadius: 14,
                            border: themeMode === "dark"
                              ? "1px solid rgba(96,165,250,.18)"
                              : "1px solid rgba(28,82,155,.10)",
                            background: themeMode === "dark"
                              ? "rgba(255,255,255,.025)"
                              : "#fff",
                          }}
                        >
                          <div style={{ fontWeight: 900 }}>{staffName}</div>
                          <div style={{ ...styles.gatewayText, marginTop: 4 }}>
                            {roleName ? `${roleName} · ` : ""}
                            {outstandingPeriod && clockIn ? `${format(clockIn, "dd MMM yyyy")} · ` : ""}
                            {clockIn ? format(clockIn, "HH:mm") : "—"}
                            {" → "}
                            {clockOut ? format(clockOut, "HH:mm") : "On shift"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={styles.gatewayCard}>
                <div style={styles.intelEyebrow}>Operational trail</div>
                <div style={{ ...styles.gatewayTitle, marginTop: 6 }}>
                  Duty & response activity
                </div>
                <div style={{ ...styles.gatewayText, marginBottom: 14 }}>
                  Duty outcomes for the selected operating period, including completed, missed and escalated activity.
                </div>

                {historyLoading && historyDutyEvents.length === 0 ? (
                  <div style={styles.gatewayText}>Loading operational history…</div>
                ) : historyDutyEvents.length === 0 ? (
                  <div style={styles.gatewayText}>No Neo duty activity recorded for this period.</div>
                ) : (
                  <div style={{ display: "grid", gap: 10 }}>
                    {historyDutyEvents.map((item) => {
                      const status = item.historyStatus;
                      const statusColor =
                        status === "completed"
                          ? "#16a374"
                          : status === "escalated"
                          ? "#f59e0b"
                          : status === "missed"
                          ? "#fb7185"
                          : themeMode === "dark"
                          ? "#7db5ff"
                          : "#2458d7";
                      const eventDate = tsToDate(item.historyEventTime);

                      return (
                        <button
                          type="button"
                          key={`history-duty-${item.id}`}
                          onClick={() => openHistoryDutyRecord(item)}
                          onMouseEnter={(event) => {
                            event.currentTarget.style.boxShadow = `0 0 0 1px ${statusColor}55, 0 0 20px ${statusColor}2e`;
                            event.currentTarget.style.borderColor = `${statusColor}88`;
                            event.currentTarget.style.transform = "translateY(-1px)";
                          }}
                          onMouseLeave={(event) => {
                            event.currentTarget.style.boxShadow = "none";
                            event.currentTarget.style.borderColor = `${statusColor}33`;
                            event.currentTarget.style.transform = "translateY(0)";
                          }}
                          onMouseDown={(event) => {
                            event.currentTarget.style.boxShadow = `0 0 0 2px ${statusColor}66, 0 0 26px ${statusColor}40`;
                            event.currentTarget.style.transform = "translateY(0) scale(.998)";
                          }}
                          title={`Open ${reportDutyLabel(item)} in Standards & Duties`}
                          style={{
                            width: "100%",
                            padding: "14px",
                            borderRadius: 14,
                            border: `1px solid ${statusColor}33`,
                            background: themeMode === "dark"
                              ? "rgba(255,255,255,.025)"
                              : "#fff",
                            color: "inherit",
                            font: "inherit",
                            textAlign: "left",
                            cursor: "pointer",
                            boxShadow: "none",
                            transform: "translateY(0)",
                            transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              gap: 10,
                              flexWrap: "wrap",
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 900 }}>
                                {reportDutyLabel(item)}
                              </div>
                              <div style={{ ...styles.gatewayText, marginTop: 4 }}>
                                {reportDutySectionLabel(item)}
                                {item.historyActor ? ` · ${item.historyActor}` : ""}
                              </div>
                            </div>
                            <span
                              style={{
                                border: `1px solid ${statusColor}66`,
                                color: statusColor,
                                background: `${statusColor}12`,
                                borderRadius: 999,
                                padding: "6px 9px",
                                fontSize: 11,
                                fontWeight: 900,
                                textTransform: "capitalize",
                              }}
                            >
                              {status === "missed" ? "Missed / expired" : status}
                            </span>
                          </div>
                          {eventDate ? (
                            <div style={{ ...styles.gatewayText, marginTop: 8, fontSize: 12 }}>
                              {outstandingPeriod ? `${format(eventDate, "dd MMM yyyy")} · ` : ""}
                              {format(eventDate, "HH:mm")}
                            </div>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div style={{ ...styles.gatewayGrid, marginTop: 18 }}>
              <div style={styles.gatewayCard}>
                <div style={styles.gatewayTitle}>Standards & Duty Reports</div>
                <div style={styles.gatewayText}>
                  Inspect the submitted evidence and detailed duty records behind this operating day.
                </div>
                <button
                  type="button"
                  style={styles.inlineAction}
                  onClick={() => setReportView("standards")}
                >
                  Open duty reports →
                </button>
              </div>

              <div style={styles.gatewayCard}>
                <div style={styles.gatewayTitle}>Outstanding / Carry-over</div>
                <div style={styles.gatewayText}>
                  Historical misses stay here in history. Only still-actionable grace duties and manager decisions appear in Outstanding.
                </div>
                <button
                  type="button"
                  style={styles.inlineAction}
                  onClick={() => setReportView("outstanding")}
                >
                  Review carry-over →
                </button>
              </div>

              <div style={styles.gatewayCard}>
                <div style={styles.gatewayTitle}>Decision History</div>
                <div style={styles.gatewayText}>
                  Approvals, rejections and Neo response escalations are available as one auditable management trail.
                </div>
                <button
                  type="button"
                  style={styles.inlineAction}
                  onClick={() => setReportView("decisions")}
                >
                  Open decision history →
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {reportView === "outstanding" ? (
          <div style={styles.intelPanel}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: isPhone ? "stretch" : "flex-start",
                flexDirection: isPhone ? "column" : "row",
                gap: 14,
              }}
            >
              <div>
                <div style={styles.intelEyebrow}>Carry-over Intelligence</div>
                <div style={styles.intelTitle}>Unfinished business does not expire at midnight</div>
                <div style={styles.intelCopy}>
                  Neo carries work forward only while it is still genuinely actionable. With no period selected, this view uses the date picker above; period filters widen the view and can be toggled off.
                </div>
              </div>

              <button
                type="button"
                style={{
                  ...styles.inlineAction,
                  marginTop: 0,
                  ...(isPhone ? { width: "100%" } : {}),
                }}
                onClick={refreshCarryOver}
                disabled={carryOverLoading}
              >
                {carryOverLoading ? "Refreshing…" : "Refresh carry-over"}
              </button>
            </div>

            <div
              style={{
                ...styles.metricGrid,
                gridTemplateColumns: isPhone
                  ? "1fr"
                  : "repeat(auto-fit,minmax(170px,1fr))",
              }}
            >
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Outstanding</div>
                <div style={styles.metricValue}>{carryOverMetrics.total}</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Needs decision</div>
                <div style={{ ...styles.metricValue, color: carryOverMetrics.needsDecision ? "#fb7185" : styles.metricValue.color }}>
                  {carryOverMetrics.needsDecision}
                </div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Active grace duties</div>
                <div style={{ ...styles.metricValue, color: carryOverMetrics.actionableDuties ? "#f59e0b" : styles.metricValue.color }}>
                  {carryOverMetrics.actionableDuties}
                </div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>From yesterday</div>
                <div style={styles.metricValue}>{carryOverMetrics.fromYesterday}</div>
              </div>
            </div>

            <div
              style={{
                marginTop: 18,
                display: "grid",
                gridTemplateColumns: isPhone
                  ? "1fr"
                  : isTablet
                  ? "repeat(2,minmax(0,1fr))"
                  : "repeat(auto-fit,minmax(300px,1fr))",
                gap: 14,
              }}
            >
              {carryOverLoading && carryOverRows.length === 0 ? (
                <div style={{ ...styles.gatewayCard, gridColumn: "1 / -1" }}>
                  <div style={styles.gatewayTitle}>Checking previous operating days…</div>
                  <div style={styles.gatewayText}>Neo is looking for unresolved response duties for this store.</div>
                </div>
              ) : filteredCarryOverRows.length === 0 && filteredPendingScheduleDecisions.length === 0 ? (
                <div style={{ ...styles.gatewayCard, gridColumn: "1 / -1" }}>
                  <div style={{ ...styles.gatewayTitle, color: "#16a374" }}>No carry-over waiting</div>
                  <div style={styles.gatewayText}>
                    There are no active grace-window duties or pending manager decisions from previous operating days. Missed daily duties remain historical rather than becoming false carry-over.
                  </div>
                </div>
              ) : (
                <>
                  {filteredPendingScheduleDecisions.length > 0 ? (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={{ ...styles.gatewayTitle, marginBottom: 10 }}>
                        Pending manager decisions
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isPhone
                            ? "1fr"
                            : isTablet
                            ? "repeat(2,minmax(0,1fr))"
                            : "repeat(auto-fit,minmax(300px,1fr))",
                          gap: 14,
                        }}
                      >
                        {filteredPendingScheduleDecisions.map((row) => (
                          <div
                            key={`decision-${row.id}`}
                            style={{
                              ...styles.gatewayCard,
                              border: "1px solid rgba(251,113,133,.28)",
                              position: "relative",
                              paddingTop: 56,
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                              <div>
                                <div style={styles.gatewayTitle}>
                                  {row.staffName || "Staff member"}
                                </div>
                                <div style={{ ...styles.gatewayText, marginTop: 4 }}>
                                  {scheduleDecisionLabel(row)}
                                </div>
                              </div>
                              <span
                                style={{
                                  position: "absolute",
                                  top: 16,
                                  right: 16,
                                  border: "1px solid rgba(251,113,133,.45)",
                                  background: "rgba(251,113,133,.10)",
                                  color: "#fb7185",
                                  borderRadius: 999,
                                  padding: "7px 10px",
                                  fontSize: 11,
                                  fontWeight: 900,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Needs decision
                              </span>
                            </div>

                            <div style={{ marginTop: 14, fontSize: 12, opacity: .76 }}>
                              {row.date || "Previous operating day"}
                              {row.roleName || row.role ? ` · ${row.roleName || row.role}` : ""}
                            </div>

                            <button
                              type="button"
                              style={{
                                ...styles.inlineAction,
                                marginTop: 16,
                                width: isPhone ? "100%" : "fit-content",
                              }}
                              onClick={() => setSelectedPendingDecision(row)}
                            >
                              Review decision →
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {filteredCarryOverRows.length > 0 ? (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={{ ...styles.gatewayTitle, marginBottom: 10 }}>
                        Duties still inside grace
                      </div>
                    </div>
                  ) : null}

                  {filteredCarryOverRows.map((row) => {
                  const escalated = reportDutyIsEscalated(row);
                  const dutyDate = reportDateKey(reportDutyOperationalDate(row));
                  const owner = String(
                    row.assignedToName ||
                    row.ownerName ||
                    row?.dueNotification?.recipientNames?.[0] ||
                    ""
                  ).trim();

                  const tone = "#f59e0b";
                  const statusLabel = "Grace still open";

                  return (
                    <div
                      key={row.id}
                      style={{
                        ...styles.gatewayCard,
                        border: `1px solid ${tone}33`,
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 10,
                          flexWrap: "wrap",
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={styles.gatewayTitle}>{reportDutyLabel(row)}</div>
                          <div style={{ ...styles.gatewayText, marginTop: 4 }}>
                            {reportDutySectionLabel(row)}
                          </div>
                        </div>
                        <span
                          style={{
                            border: `1px solid ${tone}55`,
                            background: `${tone}14`,
                            color: tone,
                            borderRadius: 999,
                            padding: "7px 10px",
                            fontSize: 11,
                            fontWeight: 900,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {statusLabel}
                        </span>
                      </div>

                      <div
                        style={{
                          marginTop: 14,
                          display: "grid",
                          gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr",
                          gap: 10,
                        }}
                      >
                        <div>
                          <div style={styles.metricLabel}>Origin</div>
                          <div style={{ marginTop: 5, fontWeight: 900 }}>
                            {carryOverAgeLabel(row, now)}
                          </div>
                          <div style={{ marginTop: 3, fontSize: 12, opacity: .72 }}>
                            {dutyDate || "Previous day"}
                          </div>
                        </div>

                        <div>
                          <div style={styles.metricLabel}>Ownership</div>
                          <div style={{ marginTop: 5, fontWeight: 900 }}>
                            {owner || "No owner recorded"}
                          </div>
                          <div style={{ marginTop: 3, fontSize: 12, opacity: .72 }}>
                            Grace ends {carryOverDeadlineLabel(row)}
                          </div>
                        </div>
                      </div>

                      {row?.managerEscalation?.status || row.escalationStatus ? (
                        <div
                          style={{
                            marginTop: 14,
                            padding: 12,
                            borderRadius: 14,
                            background: `${tone}0d`,
                            border: `1px solid ${tone}22`,
                            fontSize: 12,
                            lineHeight: 1.5,
                          }}
                        >
                          Manager escalation: {String(row?.managerEscalation?.status || row.escalationStatus).replace(/_/g, " ")}
                        </div>
                      ) : null}

                      <button
                        type="button"
                        style={{
                          ...styles.inlineAction,
                          marginTop: 16,
                          width: isPhone ? "100%" : "fit-content",
                        }}
                        onClick={() => openCarryOverRecord(row)}
                      >
                        Open historical record →
                      </button>
                    </div>
                  );
                })}
                </>
              )}
            </div>

          </div>
        ) : null}

        {reportView === "decisions" ? (
          <div style={styles.intelPanel}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 14,
                alignItems: isPhone ? "stretch" : "flex-start",
                flexDirection: isPhone ? "column" : "row",
              }}
            >
              <div>
                <div style={styles.intelEyebrow}>Decision & Response History</div>
                <div style={styles.intelTitle}>Management actions {outstandingPeriod ? reportRangeHeading(outstandingPeriod, dateFilter) : `on ${dateFilter}`}</div>
                <div style={styles.intelCopy}>
                  An auditable trail of attendance approvals, rejections and Neo response escalations for the selected operating period.
                </div>
              </div>

              <button
                type="button"
                style={{
                  ...styles.inlineAction,
                  marginTop: 0,
                  ...(isPhone ? { width: "100%" } : {}),
                }}
                onClick={refreshDecisionHistory}
                disabled={decisionHistoryLoading}
              >
                {decisionHistoryLoading ? "Refreshing…" : "Refresh decisions"}
              </button>
            </div>

            <div
              style={{
                ...styles.metricGrid,
                gridTemplateColumns: isPhone
                  ? "1fr"
                  : "repeat(auto-fit,minmax(170px,1fr))",
              }}
            >
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Total actions</div>
                <div style={styles.metricValue}>{decisionHistoryEvents.length}</div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Approved</div>
                <div style={{ ...styles.metricValue, color: "#16a374" }}>
                  {decisionHistoryEvents.filter((row) => row.outcome === "approved").length}
                </div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Rejected</div>
                <div style={{ ...styles.metricValue, color: "#fb7185" }}>
                  {decisionHistoryEvents.filter((row) => row.outcome === "rejected").length}
                </div>
              </div>
              <div style={styles.metricCard}>
                <div style={styles.metricLabel}>Escalations</div>
                <div style={{ ...styles.metricValue, color: "#f59e0b" }}>
                  {decisionHistoryEvents.filter((row) =>
                    ["escalated", "failed"].includes(row.outcome)
                  ).length}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 20 }}>
              {decisionHistoryLoading && decisionHistoryEvents.length === 0 ? (
                <div style={styles.gatewayCard}>Loading decision history…</div>
              ) : decisionHistoryEvents.length === 0 ? (
                <div style={styles.gatewayCard}>
                  <div style={styles.gatewayTitle}>No management actions recorded</div>
                  <div style={styles.gatewayText}>
                    There are no approved/rejected attendance exceptions or Neo manager escalations recorded for this period.
                  </div>
                </div>
              ) : (
                <div style={{ display: "grid", gap: 12 }}>
                  {decisionHistoryEvents.map((event) => {
                    const eventColor =
                      event.outcome === "approved"
                        ? "#16a374"
                        : event.outcome === "rejected" || event.outcome === "failed"
                        ? "#fb7185"
                        : "#f59e0b";

                    return (
                      <div
                        key={event.id}
                        style={{
                          padding: isPhone ? 14 : 16,
                          borderRadius: 16,
                          border: `1px solid ${eventColor}40`,
                          background:
                            themeMode === "dark"
                              ? "rgba(255,255,255,.025)"
                              : "#fff",
                          display: "grid",
                          gridTemplateColumns: isPhone
                            ? "1fr"
                            : "minmax(0,1fr) auto",
                          gap: 14,
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <div style={{ fontWeight: 950, fontSize: 17 }}>
                              {event.subject}
                            </div>
                            <span
                              style={{
                                border: `1px solid ${eventColor}66`,
                                color: eventColor,
                                background: `${eventColor}12`,
                                borderRadius: 999,
                                padding: "5px 8px",
                                fontSize: 11,
                                fontWeight: 900,
                                textTransform: "capitalize",
                              }}
                            >
                              {event.outcome}
                            </span>
                          </div>

                          <div style={{ marginTop: 6, fontWeight: 850 }}>
                            {event.title}
                          </div>
                          <div style={{ ...styles.gatewayText, marginTop: 5 }}>
                            {event.roleName ? `${event.roleName} · ` : ""}
                            {event.detail}
                          </div>
                          <div style={{ ...styles.gatewayText, marginTop: 7, fontSize: 12 }}>
                            {event.actor ? `By ${event.actor}` : "Recorded by Neo"}
                          </div>
                        </div>

                        <div
                          style={{
                            fontWeight: 950,
                            fontSize: 16,
                            color:
                              themeMode === "dark" ? "#dbeafe" : "#12366a",
                            textAlign: isPhone ? "left" : "right",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {event.time
                            ? `${outstandingPeriod ? `${format(event.time, "dd MMM yyyy")} · ` : ""}${format(event.time, "HH:mm")}`
                            : "—"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ) : null}

        {reportView === "standards" ? (
        <div style={styles.sections}>
          <div
            style={{
              padding: isPhone ? "16px 18px" : "18px 22px",
              marginBottom: 14,
              borderRadius: 18,
              border: themeMode === "dark"
                ? "1px solid rgba(96,165,250,.18)"
                : "1px solid rgba(28,82,155,.10)",
              background: themeMode === "dark"
                ? "rgba(255,255,255,.025)"
                : "#fff",
            }}
          >
            <div style={styles.intelEyebrow}>Standards & Duty Reports</div>
            <div style={{ ...styles.gatewayTitle, marginTop: 6 }}>
              {outstandingPeriod
                ? `Reports from ${reportRangeHeading(outstandingPeriod, dateFilter)}`
                : `Reports from ${dateFilter}`}
            </div>
            <div style={{ ...styles.gatewayText, marginTop: 5 }}>
              Clocked hours, completed submissions and in-progress duty records for the selected reporting period.
            </div>
          </div>
          {sections.map((sec, idx) => (
            <React.Fragment key={sec.id || `${sec.name}-${idx}`}>
              <button
                id={`neo-report-section-${sec.id}`}
                onClick={() => toggleSection(sec.id)}
                style={{
                  ...styles.sectionBtn,
                  ...(expandedSection === sec.id ? styles.sectionActive : {}),
                }}
              >
                {idx === 0 ? "Clocked Hours" : sec.name}
              </button>

              {expandedSection === sec.id && (
                <div style={styles.contentBox}>
                  {sec.id === CLOCKIN_VIRTUAL_ID ? (
                    (() => {
                      const isPeriodView = Boolean(outstandingPeriod);
                      const displayClockinData = isPeriodView
                        ? Object.values(filteredClockinData.reduce((groups, shift) => {
                            const name = String(shift.name || "—").trim() || "—";
                            const key = name.toLowerCase();
                            const ci = tsToDate(shift.clockIn);
                            const co = tsToDate(shift.clockOut);
                            const minutes = ci && co ? Math.max(0, (co - ci) / 60000) : 0;
                            if (!groups[key]) groups[key] = { id: `period-${key}`, name, totalMinutes: 0, shiftCount: 0 };
                            groups[key].totalMinutes += minutes;
                            groups[key].shiftCount += 1;
                            return groups;
                          }, {}))
                          .sort((a, b) => a.name.localeCompare(b.name))
                        : filteredClockinData;

                      const totalMin = isPeriodView
                        ? displayClockinData.reduce((sum, row) => sum + row.totalMinutes, 0)
                        : filteredClockinData.reduce((sum, shift) => {
                            const ci = tsToDate(shift.clockIn);
                            const co = tsToDate(shift.clockOut);
                            if (!ci || !co) return sum;
                            return sum + (co - ci) / 60000;
                          }, 0);

                      return (
                        <div style={styles.tableWrap}>
                          <table style={styles.clockTable}>
                            <thead>
                              <tr>
                                <th style={styles.thLeft}>Staff</th>
                                <th style={styles.thMid}>In</th>
                                <th style={styles.thMid}>Out</th>
                                <th style={styles.thRight}>Duration</th>
                              </tr>
                            </thead>
                            <tbody>
                              {displayClockinData.map((s) => {
                                const ci = isPeriodView ? null : tsToDate(s.clockIn);
                                const co = isPeriodView ? null : tsToDate(s.clockOut);
                                const hrs = ci && co ? Math.floor((co - ci) / 3600000) : 0;
                                const mins = ci && co ? Math.floor(((co - ci) % 3600000) / 60000) : 0;
                                const breakSummary = isPeriodView ? "" : formatBreakSummary(s);

                                return (
                                  <tr key={s.id}>
                                    <td style={styles.tdLeft}>{s.name || "—"}</td>
                                    <td style={styles.tdMid}>{ci ? format(ci, "HH:mm") : "—"}</td>
                                    <td style={styles.tdMid}>{co ? format(co, "HH:mm") : "—"}</td>
                                    <td style={styles.tdRight}>
                                      <div style={styles.durationCell}>
                                        <div style={styles.durationMain}>
                                          {isPeriodView
                                            ? formatHoursMinutesOnly(s.totalMinutes)
                                            : (ci && co ? formatHoursMinutesOnly(hrs * 60 + mins) : "—")}
                                        </div>
                                        {breakSummary ? (
                                          <div
                                            style={
                                              s.lunchTaken === false
                                                ? styles.durationMetaWarn
                                                : styles.durationMeta
                                            }
                                          >
                                            {breakSummary}
                                          </div>
                                        ) : null}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                              {!!displayClockinData.length && (
                                <tr>
                                  <td style={{ ...styles.tdLeft, fontWeight: 800 }}>Total:</td>
                                  <td style={styles.totalRowSpacer} />
                                  <td style={styles.totalRowSpacer} />
                                  <td style={styles.totalRowDuration}>
                                    <span style={styles.totalDurationValue}>
                                      {formatHoursMinutesOnly(totalMin)}
                                    </span>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()
                  ) : ([...(liveDrafts[sec.id] || []), ...(submissions[sec.id] || [])].length) ? (
                    [...(liveDrafts[sec.id] || []), ...(submissions[sec.id] || [])].map((sub) => {
                      const ts = tsToDate(sub.submittedAt) || tsToDate(sub.timestamp) || tsToDate(sub.updatedAt) || tsToDate(sub.savedAt);
                      const open = expandedSubmission[sec.id] === sub.id;
                      const label = completionLabel(sub);
                      const statusStyle = sectionStatusKind(sub) === "completed" ? styles.statusCompleted : styles.statusIncomplete;
                      const editor = sub.__liveDraft ? (sub.updatedBy || sub.savedBy || "Unknown") : (sub.submittedBy || "Unknown");
                      const progressStats = completionStatsForSection(sec, sub);

                      return (
                        <div id={`neo-report-submission-${sub.id}`} key={sub.id} style={styles.card}>
                          <div style={styles.headerRow}>
                            <div style={styles.headerLeft}>
                              <button
                                style={styles.tsBtn}
                                onClick={() => toggleSubmission(sec.id, sub.id)}
                              >
                                {ts ? format(ts, "dd MMM yyyy, HH:mm") : "(no timestamp)"}
                              </button>
                              <span style={{ ...styles.statusPill, ...statusStyle }}>{label}</span>
                              {progressStats.total ? (
                                <span style={styles.progressPill}>{formatProgress(progressStats)} done</span>
                              ) : null}
                            </div>
                            <div style={styles.liveMeta}>
                              {sub.__liveDraft ? "Last edited" : "Submitted"} by {editor}
                            </div>
                          </div>

                          {open && (
                            <div style={styles.innerBox}>
                              <div style={styles.rowMeta}>
                                <div style={styles.small}>{sub.__liveDraft ? "Last edited by:" : "Submitted by:"}</div>
                                <div style={{ ...styles.bold, color: "#1e90ff" }}>
                                  {editor}
                                </div>
                              </div>
                              {renderSubmission(sec, sub)}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p style={styles.empty}>
                      <em>{outstandingPeriod ? "No submissions in this period." : "No submissions on this date."}</em>
                    </p>
                  )}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        ) : null}

        <button
          style={{
            ...styles.backBtn,
            ...(isPhone ? { fontSize: 17, padding: "15px 16px", borderRadius: 16 } : {}),
          }}
          onClick={() => navigate("/manager-dashboard")}
        >
          ← Back
        </button>
      </div>


      {selectedPendingDecision ? (
        <div
          onClick={() => !decisionSaving && setSelectedPendingDecision(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1200,
            background: "rgba(4,12,25,.64)",
            backdropFilter: "blur(8px)",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: isPhone ? "100%" : isTablet ? "78%" : 570,
              maxWidth: "100vw",
              height: "100%",
              overflowY: "auto",
              background: themeMode === "dark" ? "#071a33" : "#f7fbff",
              color: themeMode === "dark" ? "#f5f8ff" : "#0b2b59",
              borderLeft: themeMode === "dark"
                ? "1px solid rgba(96,165,250,.22)"
                : "1px solid rgba(28,82,155,.14)",
              padding: isPhone
                ? "20px 18px calc(24px + env(safe-area-inset-bottom))"
                : 30,
              boxSizing: "border-box",
            }}
          >
            <div style={{display:"flex",justifyContent:"space-between",gap:16,alignItems:"flex-start"}}>
              <div>
                <div style={{fontSize:14,letterSpacing:".06em",textTransform:"uppercase"}}>Schedule Intelligence</div>
                <div style={{fontSize:isPhone?28:34,fontWeight:900,marginTop:6}}>
                  {selectedPendingDecision.staffName || "Staff member"}
                </div>
                <div style={{opacity:.72,marginTop:6}}>
                  {selectedPendingDecision.roleName || "Role not set"} · {storeName}
                </div>
              </div>
              <button
                type="button"
                disabled={decisionSaving}
                onClick={() => setSelectedPendingDecision(null)}
                style={{
                  width:44,height:44,borderRadius:14,
                  border:"1px solid rgba(148,163,184,.28)",
                  background:"transparent",color:"inherit",fontSize:22,cursor:"pointer"
                }}
              >×</button>
            </div>

            <div style={{
              marginTop:28,padding:20,borderRadius:18,
              border:"1px solid rgba(251,191,36,.38)",
              background:"rgba(251,191,36,.08)"
            }}>
              <div style={{fontSize:12,fontWeight:900,letterSpacing:".08em",textTransform:"uppercase",opacity:.72}}>
                Neo Intelligence
              </div>
              <div style={{fontSize:24,fontWeight:900,color:"#f59e0b",marginTop:10}}>
                {scheduleDecisionLabel(selectedPendingDecision)}
              </div>
              <div style={{marginTop:8,fontWeight:800,opacity:.72}}>Pending review</div>
            </div>

            <div style={{
              display:"grid",
              gridTemplateColumns:isPhone?"1fr":isTablet?"1fr 1fr":"1fr 1fr 1fr",
              gap:18,marginTop:22
            }}>
              <div>
                <div style={styles.metricLabel}>Published schedule</div>
                <div style={{fontSize:28,fontWeight:900,marginTop:8}}>
                  {selectedPendingDecision.scheduledText || "Not scheduled"}
                </div>
              </div>
              <div>
                <div style={styles.metricLabel}>Actual attendance</div>
                <div style={{fontSize:22,fontWeight:900,marginTop:8}}>
                  {reportActualAttendanceLabel(selectedPendingDecision)}
                </div>
              </div>
              <div>
                <div style={styles.metricLabel}>Operating date</div>
                <div style={{fontSize:22,fontWeight:900,marginTop:8}}>
                  {selectedPendingDecision.date || "—"}
                </div>
              </div>
            </div>

            <div style={{
              marginTop:22,padding:20,borderRadius:18,
              border:themeMode==="dark"?"1px solid rgba(96,165,250,.20)":"1px solid rgba(28,82,155,.12)",
              background:themeMode==="dark"?"rgba(255,255,255,.03)":"#fff"
            }}>
              <div style={styles.metricLabel}>Exception</div>
              <div style={{fontWeight:900,fontSize:18,marginTop:9}}>
                {scheduleDecisionLabel(selectedPendingDecision)}
              </div>
              <div style={{marginTop:10,opacity:.72,lineHeight:1.55}}>
                This uses the same schedule decision action as Neo Manager Schedule Intelligence.
              </div>
            </div>

            <div style={{
              display:"grid",
              gridTemplateColumns:isPhone?"1fr":"1fr 1fr",
              gap:12,marginTop:20
            }}>
              <button
                type="button"
                disabled={decisionSaving}
                onClick={() => decidePendingScheduleException(selectedPendingDecision, true)}
                style={{
                  border:"1px solid rgba(52,211,153,.44)",
                  borderRadius:14,padding:"15px 16px",
                  background:"rgba(52,211,153,.13)",color:"#16a374",
                  fontWeight:900,cursor:decisionSaving?"wait":"pointer"
                }}
              >
                {decisionSaving ? "Saving…" : selectedPendingDecision.exceptionType === "unscheduled" ? "Approve shift" : "Approve overtime"}
              </button>
              <button
                type="button"
                disabled={decisionSaving}
                onClick={() => decidePendingScheduleException(selectedPendingDecision, false)}
                style={{
                  border:"1px solid rgba(251,113,133,.44)",
                  borderRadius:14,padding:"15px 16px",
                  background:"rgba(251,113,133,.10)",color:"#fb7185",
                  fontWeight:900,cursor:decisionSaving?"wait":"pointer"
                }}
              >
                {decisionSaving ? "Saving…" : selectedPendingDecision.exceptionType === "unscheduled" ? "Reject shift" : "Reject overtime"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: "", type: "info" })}
      />
    </div>
  );
}
